import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT, TILE_SIZE, BASE_HP, COMPONENT_SPACING, TileType, EnemyType, ENEMY_STATS, ENEMY_TEXTURES, DecorInteraction, DECOR_INTERACTIONS, DECOR_RESOURCE_YIELDS } from '@/config/GameConfig';
import { ResourceManager, ResourceState } from '@/systems/ResourceManager';
import { MAP_GRID, DECOR_INDEX, decorKey, BASE_POSITIONS, getActiveMap, getDecorAt } from '@/map/MapData';
import { CustomMapStore } from '@/systems/CustomMapStore';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { EventBus, GameEvents } from '@/events/EventBus';
import { WaveManager } from '@/systems/WaveManager';
import { RobotManager } from '@/systems/RobotManager';
import { GameScene, GamePhase } from '@/scenes/GameScene';
import { Robot } from '@/entities/Robot';
import { JuiceEffects } from '@/systems/JuiceEffects';
import { RobotDesign, WorkshopEditData } from '@/scenes/WorkshopScene';
import { DesignStore } from '@/systems/DesignStore';
import { SavedDesign } from '@/systems/SlotManager';
import { SoundManager } from '@/systems/SoundManager';
import { drawRobotPreview } from '@/systems/ComponentIcons';
import { MiniProfiler } from '@/systems/MiniProfiler';
import { isDevMode, DEV_MODE_CHANGED, devNukeMode, devDestroyMode, devMoveMode } from '@/systems/DevConsole';
import { TECH, drawTechRect, fillTechRect, createTechButton, createSmallTechButton, drawMetalPlate, showTextInputDialog } from '@/ui/TechUI';
import { createButton } from '@/ui/UISwitch';
// Tutorial system archived → replaced by cinematic trigger system
// import { TutorialManager } from '@/tutorial/TutorialManager';
// import { WorkshopTutorialManager } from '@/tutorial/WorkshopTutorialManager';
import { CinematicDirector, getSequence, getSequenceIds } from '@/cinematic/CinematicDirector';
import { CinematicEvents } from '@/cinematic/CinematicEvents';
import { resetAllCinematicCompletions } from '@/cinematic/CinematicState';
import { CinematicTriggerManager } from '@/cinematic/CinematicTriggerManager';
import { UITargetRegistry } from '@/cinematic/UITargetRegistry';
import '@/cinematic/sequences/demo-wave-1'; // self-registers the sequence
import { GOD } from '@/config/GodMode';
import {
  ComponentType,
  COMPONENT_DEFS,
} from '@/components/ComponentDefs';
import {
  BehaviorRule,
  ConditionType,
  ActionType,
  TargetModifier,
} from '@/behaviors/BehaviorDefs';
import { WaveEntry } from '@/systems/WaveComposer';
import { AchievementDef, TIER_COLORS } from '@/config/AchievementDefs';
import { SaveManager } from '@/systems/SaveManager';
import { t } from '@/i18n/I18n';
import { GodModePanel } from '@/scenes/ui/GodModePanel';
import { RobotInspector } from '@/scenes/ui/RobotInspector';
import { GameOverOverlay } from '@/scenes/ui/GameOverOverlay';
import { WorkshopPanel } from '@/scenes/ui/WorkshopPanel';
import { RosterPanel } from '@/scenes/ui/RosterPanel';

// ── Preset robot templates for quick build ──
interface RobotPreset {
  name: string;
  components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[];
  rules: BehaviorRule[];
}

/** Compute preset cost from component definitions (always up to date). */
function presetCost(preset: RobotPreset): number {
  return preset.components.reduce((sum, c) => sum + COMPONENT_DEFS[c.type].cost, 0);
}

/** Effective display cost: applies GOD freeBuilding + componentCostMult. */
function displayCost(rawCost: number): number {
  return GOD.freeBuilding ? 0 : Math.round(rawCost * GOD.componentCostMult);
}

const CS = COMPONENT_SPACING; // shorthand for component offsets

const PRESETS: RobotPreset[] = [
  {
    name: 'Turret',
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.BATTERY, lx: -CS, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -CS },
      { type: ComponentType.CANNON_LIGHT, lx: CS, ly: 0 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.NO_ENEMY_DETECTED, action: ActionType.HOLD_POSITION },
    ],
  },
];

export class UIScene extends Phaser.Scene {
  // HUD elements
  private waveText!: Phaser.GameObjects.Text;
  private enemiesText!: Phaser.GameObjects.Text;
  private baseHpText!: Phaser.GameObjects.Text;
  private baseHpBarBg!: Phaser.GameObjects.Rectangle;
  private baseHpBar!: Phaser.GameObjects.Rectangle;
  private baseHpBarBorder!: Phaser.GameObjects.Rectangle;
  private baseHpSegments: Phaser.GameObjects.Rectangle[] = [];
  private baseHpSegmentBlink?: Phaser.Tweens.Tween;
  private hpBarFrame!: Phaser.GameObjects.Image;
  private hpBarFrameCx = 0; // bar center X (set in createHUD)
  private hpBarFrameCy = 0; // bar center Y
  private _lastBarX = 0;
  private _lastBarW = 0;
  private _lastBarH = 0;
  private coinIcon!: Phaser.GameObjects.Image;
  private resourcesText!: Phaser.GameObjects.Text;
  private coIcon!: Phaser.GameObjects.Image;
  private coText!: Phaser.GameObjects.Text;
  private electroIcon!: Phaser.GameObjects.Image;
  private electroText!: Phaser.GameObjects.Text;
  private phaseText!: Phaser.GameObjects.Text;
  private startButton!: Phaser.GameObjects.Container;

  // Workshop panel elements
  private workshopPanel!: Phaser.GameObjects.Container;
  private presetButtons: Phaser.GameObjects.Container[] = [];
  private presetCostTexts: Phaser.GameObjects.Text[] = [];

  // Deployed robots roster
  private rosterContainer!: Phaser.GameObjects.Container;
  private rosterScrollContent!: Phaser.GameObjects.Container;
  private rosterItems: Phaser.GameObjects.Container[] = [];
  private lastRobotCount: number = 0;
  private rosterTickCounter: number = 0;
  private rosterScrollY: number = 0;
  private rosterMaxScroll: number = 0;
  private rosterMask: Phaser.Display.Masks.GeometryMask | null = null;
  private rosterHeaderText!: Phaser.GameObjects.Text;

  // Saved designs section
  private savedDesignsContainer!: Phaser.GameObjects.Container;
  private savedDesignItems: Phaser.GameObjects.Container[] = [];
  private lastDesignCount: number = 0;

  // Inspector panel
  private sellIcon!: Phaser.GameObjects.Container; // small sell icon shown above selected robot
  private cancelIcon!: Phaser.GameObjects.Container; // cancel icon (full refund, just-placed robot)
  private repairIcon!: Phaser.GameObjects.Container; // small repair icon shown above selected robot
  private rebuildIcon!: Phaser.GameObjects.Container; // rebuild icon (restore ejected components)
  private modifyIcon!: Phaser.GameObjects.Container;  // modify icon (open workshop editor)
  private lastPlacedRobotId: string | null = null;    // ID of the most recently placed robot (for CANCEL)
  private repairAllBtn: Phaser.GameObjects.Container | null = null; // REPAIR ALL button below roster

  // Base inspector panel (shown when clicking on player base)
  private baseInspectorPanel!: Phaser.GameObjects.Container;

  // Placement ghost (visual preview that follows cursor on the map)
  private placementGhost: Phaser.GameObjects.Container | null = null;
  private anchorZoneCircles: Phaser.GameObjects.Arc[] = [];
  private cancelPlacingBtn!: Phaser.GameObjects.Container;
  private placementPreset: RobotPreset | null = null;
  private placementDesign: RobotDesign | null = null;
  private placementDesignName: string = '';

  // Editing saved design tracking
  private editingDesignIndex: number = -1;

  // Combat deploy panel (shown when clicking base during WAVE)
  private combatDeployPanel!: Phaser.GameObjects.Container;
  private combatDeployItems: Phaser.GameObjects.Container[] = [];

  // References
  private waveManager!: WaveManager;
  private robotManager!: RobotManager;
  private gameScene!: GameScene;
  // private tutorialManager!: TutorialManager; // archived
  private cinematicDirector!: CinematicDirector;
  private cinematicTriggerManager!: CinematicTriggerManager;
  private baseHp: number = BASE_HP;
  private baseMaxHp: number = BASE_HP;
  private resources: number = 100;
  private gameOver: boolean = false;
  private phase: GamePhase = GamePhase.WORKSHOP;

  // Selected robot for inspection
  private selectedRobot: Robot | null = null;

  // Speed control
  private speedButton!: Phaser.GameObjects.Container;
  private speedText!: Phaser.GameObjects.Text;
  private gameSpeed: number = 0.5;

  // Wave clear overlay
  private waveClearOverlay!: Phaser.GameObjects.Container;

  // Persistent wave preview (visible during WORKSHOP/INTERMISSION)
  private wavePreviewContainer: Phaser.GameObjects.Container | null = null;

  // Pause menu
  private pauseMenu: Phaser.GameObjects.Container | null = null;
  private paused: boolean = false;
  private fpsText!: Phaser.GameObjects.Text;
  private profilerText!: Phaser.GameObjects.Text;

  // Sound controls
  private soundPanel!: Phaser.GameObjects.Container;
  private soundPanelOpen: boolean = false;

  // God Mode panel

  // Right-click drag detection (short click = cancel, drag = camera pan)
  private rightClickStart: { x: number; y: number } | null = null;

  private fxSliderKnob!: Phaser.GameObjects.Rectangle;
  private musicSliderKnob!: Phaser.GameObjects.Rectangle;
  private fxValueText!: Phaser.GameObjects.Text;
  private musicValueText!: Phaser.GameObjects.Text;

  // Achievement toast
  private achievementQueue: { id: string; badge: string; def: any }[] = [];
  private toastProcessing = false;

  // Extracted modules
  private godModePanel!: GodModePanel;
  private robotInspector!: RobotInspector;
  private gameOverOverlay!: GameOverOverlay;
  private workshopPanelModule!: WorkshopPanel;
  private rosterPanelModule!: RosterPanel;

  // Cursor context tooltip (follows pointer, shows available actions)
  private cursorHint: Phaser.GameObjects.Text | null = null;
  private lastHintText: string = '';
  private lastHintCol: number = -1;
  private lastHintRow: number = -1;

  /** True if cinematic is active but NOT dispatching a fakeClick — used to block normal input */
  private get cinematicBlocked(): boolean {
    if (this.registry.get('cinematicDispatching')) return false; // fakeClick dispatch bypasses
    return !!(this.registry.get('cinematicActive') || this.registry.get('cinematicEditorActive'));
  }

  constructor() {
    super({ key: 'UIScene' });
  }

  create(): void {
    // Hook shutdown to scene stop event so cleanup runs on scene.stop() / restart
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    // Reset state for fresh game
    const playerBase = this.registry.get('playerBase') as { hp: number; maxHp: number } | undefined;
    this.baseMaxHp = playerBase?.maxHp ?? BASE_HP;
    this.baseHp = playerBase?.hp ?? this.baseMaxHp;
    this.resources = 100;
    this.gameOver = false;
    this.gameSpeed = 0.5;
    this.paused = false;
    this.pauseMenu = null;
    this.phase = GamePhase.WORKSHOP;
    this.selectedRobot = null;
    this.placementGhost = null;
    this.placementPreset = null;
    this.placementDesign = null;
    this.placementDesignName = '';
    this.editingDesignIndex = -1;
    this.rosterItems = [];
    this.presetButtons = [];
    this.presetCostTexts = [];
    this.savedDesignItems = [];
    this.lastRobotCount = 0;
    this.lastDesignCount = 0;
    this.rosterTickCounter = 0;
    this.soundPanelOpen = false;

    // Ensure camera matches the active map dimensions
    this.cameras.main.setViewport(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // FPS counter (top-right)
    this.fpsText = this.add.text(CANVAS_WIDTH - 10, 6, '', {
      fontSize: '12px', color: '#ffffff', fontFamily: 'monospace',
    }).setOrigin(1, 0).setDepth(999);

    // Mini profiler overlay (toggle with F3)
    this.profilerText = this.add.text(CANVAS_WIDTH - 10, 24, '', {
      fontSize: '9px', color: '#00ff88', fontFamily: 'monospace',
      backgroundColor: '#000000aa', padding: { x: 4, y: 3 },
    }).setOrigin(1, 0).setDepth(999).setVisible(false);
    this.input.keyboard?.on('keydown-F3', () => MiniProfiler.toggle());

    // Cursor context hint (follows pointer, shows available actions)
    this.cursorHint = this.add.text(0, 0, '', {
      fontSize: '11px', color: '#ccddff', fontFamily: 'monospace',
      backgroundColor: '#000000cc', padding: { x: 5, y: 3 },
      stroke: '#000000', strokeThickness: 1,
    }).setOrigin(0, 1).setDepth(10000).setVisible(false).setAlpha(0.9);

    // Delayed reference grab
    this.time.delayedCall(100, () => {
      this.waveManager = this.registry.get('waveManager') as WaveManager;
      this.robotManager = this.registry.get('robotManager') as RobotManager;
      this.gameScene = this.registry.get('gameScene') as GameScene;
    });

    // ── Instantiate extracted modules ──
    // GodModePanel, RobotInspector, GameOverOverlay contain the authoritative implementations.
    // UIScene delegates to them via thin wrapper methods.
    this.godModePanel = new GodModePanel(
      this,
      () => this.gameScene,
      () => this.robotManager,
      () => this.waveManager,
      () => this.refreshGodVisuals(),
    );
    this.godModePanel.setup();

    this.robotInspector = new RobotInspector(this, {
      getPhase: () => this.phase,
      getResources: () => this.resources,
      getGameScene: () => this.gameScene,
      getRobotManager: () => this.robotManager,
      getSelectedRobot: () => this.selectedRobot,
      setSelectedRobot: (r) => { this.selectedRobot = r; },
      getLastPlacedRobotId: () => this.lastPlacedRobotId,
      setLastPlacedRobotId: (id) => { this.lastPlacedRobotId = id; },
      isPlacing: () => !!(this.placementPreset || this.placementDesign),
      refreshRoster: () => this.refreshRoster(),
      refreshSavedDesigns: () => this.refreshSavedDesigns(),
      refreshSensorForSelection: (r) => this.refreshSensorForSelection(r),
      openWorkshopForEdit: (r) => this.openWorkshopForEdit(r),
      displayCost: (c) => displayCost(c),
      buildBaseHpSegments: (x, y, w, h) => this.buildBaseHpSegments(x, y, w, h),
      animateBaseHpBarUpgrade: (a, b, c, d) => this.animateBaseHpBarUpgrade(a, b, c, d),
      getBaseHpBarBg: () => this.baseHpBarBg,
      setBaseHp: (hp) => { this.baseHp = hp; },
      setBaseMaxHp: (maxHp) => { this.baseMaxHp = maxHp; },
    });
    this.robotInspector.setup();

    this.gameOverOverlay = new GameOverOverlay(this);
    this.gameOverOverlay.setup();

    this.workshopPanelModule = new WorkshopPanel();
    this.workshopPanelModule.setup();

    this.rosterPanelModule = new RosterPanel();
    this.rosterPanelModule.setup();

    this.createHUD();
    this.createSoundPanel();
    this.createGodModeButton();
    this.createCinematicEditorButton();
    this.createWorkshopPanel();
    this.createCombatDeployPanel();
    this.createCancelPlacingButton();
    this.createSellIcon();
    this.createCancelIcon();
    this.createRepairIcon();
    this.createRebuildIcon();
    this.createModifyIcon();
    this.createBaseInspectorPanel();
    this.setupInputHandlers();
    this.setupEventListeners();
    // Show initial wave preview (game starts in WORKSHOP phase)
    this.time.delayedCall(200, () => this.refreshWavePreview());
    // Apply sensor overlay limit (hides sensors when too many robots are deployed)
    this.time.delayedCall(100, () => this.setSensorCirclesVisible(true));

    // Achievement toast system
    this.setupAchievementToast();

    // Cinematic system — Director + Trigger Manager
    const gameSceneRef = this.scene.get('GameScene') as GameScene;
    this.cinematicDirector = new CinematicDirector(gameSceneRef, this);
    this.cinematicTriggerManager = new CinematicTriggerManager(this);
    // Fire mapStart triggers (replaces hardcoded demo-wave-1 check)
    this.cinematicTriggerManager.fireMapStart();

    // Tutorial replaced by cinematic trigger system (see map.cinematics + CinematicTriggerManager)
  }

  // ── HUD (top bar) ──

  private createHUD(): void {
    const y = 6;
    const s: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '15px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    };

    this.waveText = this.add.text(10, y, t('hud.wave', { n: 0 }), s).setDepth(100);
    this.enemiesText = this.add.text(GOD.hudEnemiesX, y, t('hud.enemies', { n: 0 }), s).setDepth(100);
    this.baseHpText = this.add.text(GOD.hudBaseX, y, t('hud.base'), s).setDepth(100);
    // Base HP segmented bar (right after "Base:" label)
    const barX = GOD.hudBarX;
    const barW = GOD.hudBarW;
    const barH = GOD.hudBarH;
    const barY = y + 3;
    this.baseHpBarBg = this.add.rectangle(barX, barY, barW, barH, 0x1a1a1a).setOrigin(0, 0).setDepth(100);
    this.baseHpBar = this.add.rectangle(barX, barY, 0, 0, 0x000000, 0).setOrigin(0, 0).setDepth(101); // hidden, kept for compat
    this.baseHpBarBorder = this.add.rectangle(barX, barY, barW, barH).setOrigin(0, 0).setStrokeStyle(1, 0x888888).setFillStyle(0x000000, 0).setDepth(103);
    // Track bar dimensions for live-update diffing
    this._lastBarX = barX;
    this._lastBarW = barW;
    this._lastBarH = barH;
    // HP bar decorative frame (behind segments) — positioned independently via hpBarFrame* params
    this.hpBarFrameCx = barX + GOD.hpBarFrameW / 2;
    this.hpBarFrameCy = barY + barH / 2;
    this.hpBarFrame = this.add.image(this.hpBarFrameCx, this.hpBarFrameCy, 'hp_bar_frame')
      .setDisplaySize(GOD.hpBarFrameW, GOD.hpBarFrameH)
      .setDepth(99);
    this.buildBaseHpSegments(barX, barY, barW, barH);
    // Currency + resources with icons
    const ico = 31;
    const icoY = y + 10; // vertically centered in HUD bar
    let hx = 440;
    this.coinIcon = this.add.image(hx, icoY, 'icon_coin').setDisplaySize(ico, ico).setDepth(100);
    hx += ico / 2 + 4;
    this.resourcesText = this.add.text(hx, y, `${this.resources}`, { ...s, color: '#ffcc00' }).setDepth(100);
    hx += Math.max(40, this.resourcesText.width + 11);
    this.coIcon = this.add.image(hx + ico / 2, icoY, 'icon_co').setDisplaySize(ico, ico).setDepth(100);
    hx += ico + 4;
    this.coText = this.add.text(hx, y, `${ResourceManager.co}`, { ...s, fontSize: '13px', color: '#44ee88' }).setDepth(100);
    hx += Math.max(30, this.coText.width + 11);
    this.electroIcon = this.add.image(hx + ico / 2, icoY, 'icon_electro').setDisplaySize(ico, ico).setDepth(100);
    hx += ico + 4;
    this.electroText = this.add.text(hx, y, `${ResourceManager.electro}`, { ...s, fontSize: '13px', color: '#88ccff' }).setDepth(100);
    hx += Math.max(30, this.electroText.width + 11);
    this.phaseText = this.add.text(hx, y, t('hud.phase.workshop'), { ...s, color: '#44ff44' }).setDepth(100);

    // ── Right-side HUD buttons: [Menu] [♪ Sound] [Speed] [Start Wave] ──
    // Layout from right to left with consistent spacing
    const btnCY = y + 14;  // vertically centered in the HUD bar

    // Start Wave — rightmost, primary action
    const startBtnContainer = createButton(this, CANVAS_WIDTH - 75, btnCY, t('hud.startWave'), {
      onClick: () => { SoundManager.play('clickConfirm'); this.onStartWave(); },
      color: TECH.ACCENT, fontSize: '14px', height: 30,
      depth: 102,
    });
    this.startButton = startBtnContainer;

    // Register start button for cinematic spotlight targeting
    UITargetRegistry.register('startButton', () => {
      if (!this.startButton?.visible) return null;
      const b = this.startButton.getBounds();
      return { x: b.x, y: b.y, w: b.width, h: b.height };
    });

    // Speed toggle (visible only during combat) — left of Start Wave
    const speedW = 66;
    const speedH = 26;
    const speedContainer = this.add.container(CANVAS_WIDTH - 180, btnCY).setDepth(102).setVisible(false);
    const speedGfx = this.add.graphics();
    const speedText = this.add.text(0, 0, t('hud.speed', { n: 0.5 }), {
      fontSize: '12px', color: '#88ccff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    drawTechRect(speedGfx, -speedW / 2, -speedH / 2, speedW, speedH, 0x88ccff, 0.6, 1);
    const speedHit = this.add.rectangle(0, 0, speedW, speedH, 0x000000, 0).setInteractive({ useHandCursor: true });
    speedContainer.add([speedGfx, speedText, speedHit]);
    speedHit.on('pointerover', () => { speedGfx.clear(); drawTechRect(speedGfx, -speedW / 2, -speedH / 2, speedW, speedH, TECH.HOVER, 1, 1.5); speedText.setColor('#ffffff'); });
    speedHit.on('pointerout', () => { speedGfx.clear(); drawTechRect(speedGfx, -speedW / 2, -speedH / 2, speedW, speedH, 0x88ccff, 0.6, 1); speedText.setColor('#88ccff'); });
    speedHit.on('pointerdown', () => this.toggleSpeed());
    this.speedButton = speedContainer;
    this.speedText = speedText;

    // Pause button — opens pause menu (same as ESC)
    const menuBtnC = createSmallTechButton(this, CANVAS_WIDTH - 270, btnCY, t('hud.pause'), 0xaa8888, () => {
      SoundManager.play('click');
      this.togglePauseMenu();
    });
    menuBtnC.setDepth(102);
  }

  /** Speed cycle: 0.125 → 0.25 → 0.5 → 1 → 2 → back to 0.125 */
  private static readonly SPEED_STEPS = [0.125, 0.25, 0.5, 1, 2];

  private toggleSpeed(): void {
    SoundManager.play('click');
    const steps = UIScene.SPEED_STEPS;
    const idx = steps.indexOf(this.gameSpeed);
    this.gameSpeed = steps[(idx + 1) % steps.length];
    this.speedText.setText(t('hud.speed', { n: this.gameSpeed }));
    if (this.gameScene) {
      this.gameScene.gameSpeed = this.gameSpeed;
    }
  }

  // ── Pause Menu ──

  private togglePauseMenu(): void {
    if (this.gameOver) return;
    // Block during cinematics
    if (this.cinematicBlocked) return;
    if (this.paused) {
      this.closePauseMenu();
    } else {
      this.openPauseMenu();
    }
  }

  private openPauseMenu(): void {
    if (this.paused) return;
    this.paused = true;
    this.scene.pause('GameScene');

    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;
    this.pauseMenu = this.add.container(0, 0).setDepth(200);

    // Dim overlay — clicking outside the panel resumes the game
    const dim = this.add.rectangle(cx, cy, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.6)
      .setInteractive();
    dim.on('pointerdown', () => this.closePauseMenu());
    this.pauseMenu.add(dim);

    // Enlarged panel to fit tabs
    const pw = 420;
    const ph = 380;
    const panel = this.add.rectangle(cx, cy, pw, ph, 0x0a0a1e, 0.95)
      .setStrokeStyle(2, 0x4466aa).setInteractive();
    this.pauseMenu.add(panel);

    // ── Tab system ──
    const tabY = cy - ph / 2 + 12;
    const tabNames = [t('pause.menu'), t('pause.gameplay')];
    const tabContainers: Phaser.GameObjects.Container[] = [];
    const tabButtons: Phaser.GameObjects.Container[] = [];
    const tabW = 100;

    for (let i = 0; i < tabNames.length; i++) {
      const tx = cx - (tabNames.length * tabW) / 2 + tabW * i + tabW / 2;
      const tabBtn = this.createPauseTabButton(tx, tabY, tabNames[i], i === 0, () => {
        // Activate this tab, deactivate others
        tabContainers.forEach((tc, idx) => tc.setVisible(idx === i));
        tabButtons.forEach((tb, idx) => {
          const isActive = idx === i;
          const label = tb.getAt(1) as Phaser.GameObjects.Text;
          const underline = tb.getAt(2) as Phaser.GameObjects.Rectangle;
          label.setColor(isActive ? '#ffcc00' : '#888888');
          underline.setVisible(isActive);
        });
        SoundManager.play('click');
      });
      tabButtons.push(tabBtn);
      this.pauseMenu.add(tabBtn);
    }

    // ── MENU tab content ──
    const menuTab = this.add.container(0, 0);
    const contentY = tabY + 40;

    const title = this.add.text(cx, contentY, t('pause.paused'), {
      fontSize: '28px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    menuTab.add(title);

    const resumeBtn = this.createPauseButton(cx, contentY + 60, t('pause.resume'), '#44ff88', () => {
      this.closePauseMenu();
    });
    menuTab.add(resumeBtn);

    const restartBtn = this.createPauseButton(cx, contentY + 120, t('pause.restart'), '#ffcc44', () => {
      this.closePauseMenu();
      this.rollbackToSnapshot();
      this.scene.stop('GameScene');
      this.scene.stop('UIScene');
      this.scene.start('GameScene');
      this.scene.start('UIScene');
    });
    menuTab.add(restartBtn);

    const quitBtn = this.createPauseButton(cx, contentY + 180, t('pause.quitToTitle'), '#ff6644', () => {
      this.closePauseMenu();
      this.rollbackToSnapshot();
      const isCampaign = !!this.registry.get('currentLevelNode');
      this.scene.stop('GameScene');
      if (isCampaign) {
        this.scene.start('LevelTreeScene');
      } else {
        const isCustomMap = !!CustomMapStore.getById(getActiveMap().id);
        this.scene.start('MapSelectScene', { tab: isCustomMap ? 'CUSTOM' : 'OFFICIAL' });
      }
      this.scene.stop('UIScene');
    });
    menuTab.add(quitBtn);

    tabContainers.push(menuTab);
    this.pauseMenu.add(menuTab);

    // ── GAMEPLAY tab content ──
    const gameplayTab = this.add.container(0, 0).setVisible(false);
    const gpY = contentY + 10;
    const gpLabelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '14px', color: '#cccccc', fontFamily: 'monospace',
    };

    // Section title
    gameplayTab.add(this.add.text(cx, gpY, t('pause.gameOptions'), {
      fontSize: '20px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Cinematic reset button (replaces old tutorial reset)
    const cinematicBtn = this.createPauseButton(cx, gpY + 60, t('pause.resetCinematics'), '#44ff88', () => {
      resetAllCinematicCompletions();
      const label = cinematicBtn.getAt(1) as Phaser.GameObjects.Text;
      label.setText(t('pause.cinematicsReset'));
      label.setColor('#ffcc00');
    });
    gameplayTab.add(cinematicBtn);

    // ── Context help toggle ──
    const contextOn = SaveManager.getSettings().contextHelp;
    const ctxLabel = this.add.text(cx - 70, gpY + 140, t('pause.contextHelp'), {
      ...gpLabelStyle, fontSize: '13px',
    }).setOrigin(0, 0.5);
    gameplayTab.add(ctxLabel);

    const ctxState = this.add.text(cx + 70, gpY + 140, contextOn ? 'ON' : 'OFF', {
      fontSize: '14px', color: contextOn ? '#44ff88' : '#ff6644',
      fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    gameplayTab.add(ctxState);

    const ctxHit = this.add.rectangle(cx, gpY + 140, 260, 28, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    ctxHit.on('pointerdown', () => {
      SoundManager.play('click');
      const current = SaveManager.getSettings().contextHelp;
      SaveManager.updateSettings({ contextHelp: !current });
      ctxState.setText(!current ? 'ON' : 'OFF');
      ctxState.setColor(!current ? '#44ff88' : '#ff6644');
    });
    gameplayTab.add(ctxHit);

    tabContainers.push(gameplayTab);
    this.pauseMenu.add(gameplayTab);
  }

  /** Create a tab button for the pause menu */
  private createPauseTabButton(
    x: number, y: number, label: string, active: boolean, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const hit = this.add.rectangle(0, 0, 90, 28, 0x000000, 0).setInteractive({ useHandCursor: true });
    const text = this.add.text(0, 0, label, {
      fontSize: '13px', color: active ? '#ffcc00' : '#888888',
      fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    const underline = this.add.rectangle(0, 14, 70, 2, TECH.ACCENT).setVisible(active);
    container.add([hit, text, underline]);
    hit.on('pointerdown', onClick);
    hit.on('pointerover', () => { if (!underline.visible) text.setColor('#cccccc'); });
    hit.on('pointerout', () => { if (!underline.visible) text.setColor('#888888'); });
    return container;
  }

  private createPauseButton(x: number, y: number, label: string, color: string, onClick: () => void): Phaser.GameObjects.Container {
    const hexNum = parseInt(color.replace('#', ''), 16);
    return createButton(this, x, y, label, {
      onClick: () => { SoundManager.play('click'); onClick(); },
      color: hexNum, width: 250, height: 44, fontSize: '16px',
    });
  }

  /** Rollback progression and currency to the snapshot taken before the map started.
   *  Designs are NOT rolled back — edits/renames in the library persist regardless of win/loss. */
  private rollbackToSnapshot(): void {
    // Clear crash-recovery snapshot — player is leaving normally
    ProgressionManager.clearPreGameSnapshot();

    const snapshot = this.registry.get('levelStartSnapshot') as {
      progression: ReturnType<typeof ProgressionManager.getState>;
      resources?: ResourceState;
      designs: SavedDesign[];
    } | undefined;
    if (snapshot) {
      // Rollback but preserve permanent progression (unlocks & evolutions)
      ProgressionManager.rollbackToState(snapshot.progression);
      if (snapshot.resources) {
        ResourceManager.loadFromState(snapshot.resources);
        ResourceManager.save();
      }
      // Designs are intentionally NOT rolled back: library edits (rename, add, remove)
      // are permanent user choices that should survive win/loss/retry.
    }
  }

  private closePauseMenu(): void {
    if (!this.paused) return;
    this.paused = false;
    if (this.pauseMenu) {
      this.pauseMenu.destroy(true);
      this.pauseMenu = null;
    }
    this.scene.resume('GameScene');
  }

  // ── Sound Controls Panel ──

  private createSoundPanel(): void {
    const soundBtnX = CANVAS_WIDTH - 340;
    const soundBtnY = 20;

    // Toggle button — left of Menu
    const toggleBtn = createSmallTechButton(this, soundBtnX, soundBtnY, '♪', 0xaaccff, () => {
      SoundManager.play('click');
      this.soundPanelOpen = !this.soundPanelOpen;
      this.soundPanel.setVisible(this.soundPanelOpen);
    });
    toggleBtn.setDepth(150);

    // Dropdown panel (tech style)
    const pw = 180;
    const ph = 110;
    const panelX = soundBtnX - pw / 2;
    const panelY = soundBtnY + 18;
    this.soundPanel = this.add.container(panelX, panelY).setDepth(150).setVisible(false);

    // Dismiss backdrop — covers full screen behind the panel
    const soundDismiss = this.add.rectangle(
      -panelX + CANVAS_WIDTH / 2, -panelY + CANVAS_HEIGHT / 2,
      CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0
    ).setInteractive();
    soundDismiss.on('pointerdown', () => {
      this.soundPanelOpen = false;
      this.soundPanel.setVisible(false);
    });
    this.soundPanel.add(soundDismiss);

    const panelGfx = this.add.graphics();
    fillTechRect(panelGfx, 0, 0, pw, ph, TECH.BG, 0.95);
    drawTechRect(panelGfx, 0, 0, pw, ph, TECH.COLOR, 0.4, 1);
    this.soundPanel.add(panelGfx);

    const blocker = this.add.rectangle(pw / 2, ph / 2, pw, ph, 0x000000, 0).setInteractive();
    this.soundPanel.add(blocker);

    const labelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '11px', color: '#aaaacc', fontFamily: 'monospace',
    };

    const trackLeft = 45;
    const trackW = 80;
    const trackRight = trackLeft + trackW;

    // ── FX volume slider ──
    this.soundPanel.add(this.add.text(8, 14, t('sound.fx'), labelStyle));
    this.soundPanel.add(this.add.rectangle(trackLeft + trackW / 2, 20, trackW, 2, TECH.DIM));

    this.fxValueText = this.add.text(trackRight + 8, 14, `${Math.round(SoundManager.fxVolume * 100)}%`, labelStyle);
    this.soundPanel.add(this.fxValueText);

    this.fxSliderKnob = this.add.rectangle(
      trackLeft + SoundManager.fxVolume * trackW, 20, 10, 14, 0x000000, 0
    ).setStrokeStyle(1.5, TECH.COLOR).setInteractive({ useHandCursor: true, draggable: true });
    this.soundPanel.add(this.fxSliderKnob);

    this.input.setDraggable(this.fxSliderKnob);
    this.fxSliderKnob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => {
      const clampedX = Math.max(trackLeft, Math.min(trackRight, dragX));
      this.fxSliderKnob.x = clampedX;
      const vol = (clampedX - trackLeft) / trackW;
      SoundManager.fxVolume = vol;
      this.fxValueText.setText(`${Math.round(vol * 100)}%`);
    });

    // ── Music volume slider ──
    this.soundPanel.add(this.add.text(8, 40, t('sound.music'), labelStyle));
    this.soundPanel.add(this.add.rectangle(trackLeft + trackW / 2, 46, trackW, 2, TECH.DIM));

    this.musicValueText = this.add.text(trackRight + 8, 40, `${Math.round(SoundManager.musicVolume * 100)}%`, labelStyle);
    this.soundPanel.add(this.musicValueText);

    this.musicSliderKnob = this.add.rectangle(
      trackLeft + SoundManager.musicVolume * trackW, 46, 10, 14, 0x000000, 0
    ).setStrokeStyle(1.5, TECH.COLOR).setInteractive({ useHandCursor: true, draggable: true });
    this.soundPanel.add(this.musicSliderKnob);

    this.input.setDraggable(this.musicSliderKnob);
    this.musicSliderKnob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => {
      const clampedX = Math.max(trackLeft, Math.min(trackRight, dragX));
      this.musicSliderKnob.x = clampedX;
      const vol = (clampedX - trackLeft) / trackW;
      SoundManager.musicVolume = vol;
      this.musicValueText.setText(`${Math.round(vol * 100)}%`);
    });

    // ── Mute toggle (tech style) ──
    const muteLabel = SoundManager.muted ? t('sound.unmute') : t('sound.mute');
    const muteColor = SoundManager.muted ? TECH.SUCCESS : TECH.DANGER;
    const muteBtn = createSmallTechButton(this, pw / 2, ph - 18, muteLabel, muteColor, () => {
      SoundManager.muted = !SoundManager.muted;
      this.soundPanel.destroy();
      this.createSoundPanel();
      this.soundPanelOpen = true;
      this.soundPanel.setVisible(true);
    }, 150);
    this.soundPanel.add(muteBtn);
  }

  // ── God Mode Panel ──

  private godBtn: Phaser.GameObjects.Container | null = null;
  private _onDevModeChangedGod: ((on: boolean) => void) | null = null;
  private _onDevModeChangedCin: ((on: boolean) => void) | null = null;

  private createGodModeButton(): void {
    // Small button in top-left area (after phase text) — only visible in dev mode
    this.godBtn = createSmallTechButton(this, CANVAS_WIDTH - 410, 20, 'GOD', 0xff88ff, () => {
      SoundManager.play('click');
      this.toggleGodPanel();
    });
    this.godBtn.setDepth(150);
    this.godBtn.setVisible(isDevMode());
    // Listen for dev console toggle
    this._onDevModeChangedGod = (on: boolean) => {
      this.godBtn?.setVisible(on);
    };
    EventBus.on(DEV_MODE_CHANGED, this._onDevModeChangedGod);
  }

  private cinEditorBtn: Phaser.GameObjects.Container | null = null;

  private createCinematicEditorButton(): void {
    this.cinEditorBtn = createSmallTechButton(this, CANVAS_WIDTH - 410, CANVAS_HEIGHT - 20, t('ui.cinematic'), 0x44aaff, () => {
      SoundManager.play('click');
      if (this.scene.isActive('CinematicEditorScene')) return;
      // Determine host scene (GameScene when available, otherwise self)
      const hostKey = this.scene.isActive('GameScene') ? 'GameScene' : this.scene.key;
      const callerScenes = hostKey === 'GameScene' ? ['GameScene', 'UIScene'] : [this.scene.key];
      for (const key of callerScenes) this.scene.pause(key);
      const initData: Record<string, unknown> = { hostSceneKey: hostKey, callerScenes };
      const ids = getSequenceIds();
      if (ids.length > 0) initData.sequence = getSequence(ids[0]);
      this.scene.launch('CinematicEditorScene', initData);
    });
    this.cinEditorBtn.setDepth(150);
    this.cinEditorBtn.setVisible(isDevMode());
    this._onDevModeChangedCin = (on: boolean) => {
      this.cinEditorBtn?.setVisible(on);
    };
    EventBus.on(DEV_MODE_CHANGED, this._onDevModeChangedCin);
  }

  private toggleGodPanel(): void {
    this.godModePanel.toggle();
    if (!this.godModePanel.isOpen && !this.paused) this.scene.resume('GameScene');
  }








  /** Force-refresh GOD visual params on all existing entities (works even when paused) */
  private refreshGodVisuals(): void {
    this.godModePanel.refreshGodVisuals();
    if (this.robotManager) {
      for (const robot of this.robotManager.robots) {
        if (robot.alive) { robot.invalidateBars(); robot.updateDebugOverlays(); }
      }
    }
    if (this.waveManager) {
      for (const enemy of this.waveManager.enemies) {
        if (enemy.alive) enemy.refreshGodVisuals();
      }
    }
  }

  // ── GOD Mode Preview Sandbox ──

  /** Built-in preview designs for GOD mode (ignores component locks) */

  /** Get all preview designs: built-in GOD templates + saved user designs */


  /** Add a small action button to the preview container */

  /** Build a ◄ VALUE ► cycling selector */

  // ── Preview entity spawn/respawn ──








  // ── Workshop Panel (left side) ──

  private createWorkshopPanel(): void {
    this.workshopPanel = this.add.container(0, 0).setDepth(100);

    // Determine layout: 2 columns if many presets, 1 column if few
    const COLS = PRESETS.length > 5 ? 2 : 1;
    const COL_W = 132;
    const ROW_H = 58;
    const panelW = COLS * COL_W + 8;

    // Background — starts below HUD top bar (28px)
    const HUD_H = 28;
    const bg = this.add.rectangle(0, HUD_H, panelW, CANVAS_HEIGHT - HUD_H, 0x111122, 0.85);
    bg.setOrigin(0, 0);
    this.workshopPanel.add(bg);

    // Title
    const title = this.add.text(8, HUD_H + 4, t('workshop.units'), {
      fontSize: '12px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });
    this.workshopPanel.add(title);

    // Preset buttons
    let btnY = HUD_H + 18;
    for (let i = 0; i < PRESETS.length; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const bx = 4 + col * COL_W;
      const by = btnY + row * ROW_H;
      const btn = this.createPresetButton(PRESETS[i], bx, by);
      this.workshopPanel.add(btn);
      this.presetButtons.push(btn);
    }
    const totalRows = Math.ceil(PRESETS.length / COLS);
    btnY += totalRows * ROW_H;

    // Custom Build button
    const customBtn = createSmallTechButton(this, 8 + 50, btnY + 28, '+ Build', 0x44ccff, () => {
      SoundManager.play('clickConfirm'); this.openWorkshop();
    });
    this.workshopPanel.add(customBtn);

    // Saved designs section
    const savedY = btnY + 56;
    const savedTitle = this.add.text(8, savedY, t('workshop.myDesigns'), {
      fontSize: '10px', color: '#44ccff', fontFamily: 'monospace', fontStyle: 'bold',
    });
    this.workshopPanel.add(savedTitle);

    this.savedDesignsContainer = this.add.container(0, savedY + 16);
    this.workshopPanel.add(this.savedDesignsContainer);

    // Deployed robots roster header + scrollable container (positioned dynamically below saved designs)
    this.rosterContainer = this.add.container(0, savedY + 20);
    this.rosterHeaderText = this.add.text(8, -4, t('roster.deployed', { n: 0 }), {
      fontSize: '10px', color: '#44cccc', fontFamily: 'monospace', fontStyle: 'bold',
    });
    this.rosterContainer.add(this.rosterHeaderText);

    // Scrollable inner container for roster items
    this.rosterScrollContent = this.add.container(0, 0);
    this.rosterContainer.add(this.rosterScrollContent);
    this.rosterScrollY = 0;

    // Scroll hit zone — visual only, wheel events are handled at scene level
    const rosterHitZone = this.add.rectangle(panelW / 2, 100, panelW, 200, 0x000000, 0);
    this.rosterContainer.addAt(rosterHitZone, 0); // behind scroll content so it doesn't block clicks
    (this as any)._rosterHitZone = rosterHitZone;
    (this as any)._rosterPanelW = panelW;

    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
      if (!this.workshopPanel?.visible) return;
      // Check if pointer is within the roster area
      const rosterWorldY = this.rosterContainer.y;
      const px = _pointer.x;
      const py = _pointer.y;
      if (px > panelW || py < rosterWorldY || py > CANVAS_HEIGHT) return;
      this.rosterScrollY = Phaser.Math.Clamp(
        this.rosterScrollY - dy * 0.5,
        -this.rosterMaxScroll, 0,
      );
      this.rosterScrollContent.setY(this.rosterScrollY);
    });

    this.workshopPanel.add(this.rosterContainer);

    // Initial refresh to position correctly
    this.refreshSavedDesigns();
  }

  // ── Combat Deploy Panel (quick-deploy from base click during WAVE) ──

  private createCombatDeployPanel(): void {
    this.combatDeployPanel = this.add.container(-150, 0).setDepth(100).setVisible(false);

    const HUD_H = 28;
    const panelW = 140;
    const bg = this.add.rectangle(0, HUD_H, panelW, CANVAS_HEIGHT - HUD_H, 0x0a0a1a, 0.92)
      .setOrigin(0, 0).setStrokeStyle(1, 0x44cccc, 0.5);
    this.combatDeployPanel.add(bg);

    // Title
    const title = this.add.text(8, HUD_H + 6, '⚡ DEPLOY', {
      fontSize: '12px', color: '#44cccc', fontFamily: 'monospace', fontStyle: 'bold',
    });
    this.combatDeployPanel.add(title);

    // Close X button
    const closeBtn = this.add.text(panelW - 18, HUD_H + 4, '✕', {
      fontSize: '14px', color: '#ff6666', fontFamily: 'monospace', fontStyle: 'bold',
    }).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => { SoundManager.play('clickCancel'); this.hideCombatDeployPanel(); });
    closeBtn.on('pointerover', () => closeBtn.setColor('#ff9999'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#ff6666'));
    this.combatDeployPanel.add(closeBtn);
  }

  private showCombatDeployPanel = (): void => {
    // Only during WAVE or INTERMISSION
    if (this.phase === GamePhase.WORKSHOP) return;

    this.refreshCombatDeployItems();
    this.combatDeployPanel.setVisible(true);
    // Slide in from left
    this.tweens.killTweensOf(this.combatDeployPanel);
    this.tweens.add({
      targets: this.combatDeployPanel,
      x: 0,
      duration: 200,
      ease: 'Back.easeOut',
    });
    SoundManager.play('clickSelect');
  };

  private hideCombatDeployPanel(): void {
    if (!this.combatDeployPanel.visible) return;
    this.tweens.killTweensOf(this.combatDeployPanel);
    this.tweens.add({
      targets: this.combatDeployPanel,
      x: -150,
      duration: 150,
      ease: 'Power2',
      onComplete: () => this.combatDeployPanel.setVisible(false),
    });
  }

  private refreshCombatDeployItems(): void {
    // Clear old items
    for (const item of this.combatDeployItems) item.destroy();
    this.combatDeployItems = [];

    const HUD_H = 28;
    const ITEM_H = 50;
    const startY = HUD_H + 26;
    const designs = DesignStore.getForMode();

    if (designs.length === 0) {
      const emptyMsg = this.add.text(8, startY, 'No saved designs.\nBuild in Workshop first.', {
        fontSize: '9px', color: '#666688', fontFamily: 'monospace', wordWrap: { width: 124 },
      });
      this.combatDeployPanel.add(emptyMsg);
      this.combatDeployItems.push(emptyMsg as unknown as Phaser.GameObjects.Container);
      return;
    }

    for (let i = 0; i < designs.length; i++) {
      const saved = designs[i];
      const y = startY + i * (ITEM_H + 4);
      const item = this.add.container(4, y);

      const cost = displayCost(saved.design.cost);
      const canAfford = this.resources >= cost;
      const bgColor = canAfford ? 0x1a2a3a : 0x2a1a1a;

      const bg = this.add.rectangle(0, 0, 132, ITEM_H, bgColor, 1)
        .setOrigin(0, 0).setStrokeStyle(1, canAfford ? 0x446688 : 0x664444)
        .setInteractive({ useHandCursor: canAfford });
      item.add(bg);

      // Robot preview thumbnail
      const preview = drawRobotPreview(this, saved.design.components, 36);
      preview.setPosition(22, ITEM_H / 2);
      item.add(preview);

      // Name
      const nameLabel = this.add.text(46, 4, saved.name, {
        fontSize: '9px', color: canAfford ? '#44ccff' : '#886666', fontFamily: 'monospace', fontStyle: 'bold',
      });
      item.add(nameLabel);

      // Cost
      const costLabel = this.add.text(46, 18, `$${cost}`, {
        fontSize: '10px', color: canAfford ? '#ffcc00' : '#886644', fontFamily: 'monospace', fontStyle: 'bold',
      });
      item.add(costLabel);

      // Parts count
      const partsLabel = this.add.text(46, 32, `${saved.design.components.length}p`, {
        fontSize: '8px', color: '#666688', fontFamily: 'monospace',
      });
      item.add(partsLabel);

      bg.on('pointerover', () => { if (canAfford) bg.setFillStyle(0x2a3a4a); });
      bg.on('pointerout', () => bg.setFillStyle(bgColor));
      bg.on('pointerdown', () => {
        if (!canAfford) {
          this.showCantAffordFeedback(item);
          return;
        }
        SoundManager.play('clickConfirm');
        this.selectDesignForCombatDeploy(saved.design, saved.name);
      });

      this.combatDeployPanel.add(item);
      this.combatDeployItems.push(item);
    }
  }

  private selectDesignForCombatDeploy(design: RobotDesign, name: string): void {
    const cost = displayCost(design.cost);
    if (this.resources < cost) return;
    this.cancelPlacement();
    this.placementDesign = design;
    this.placementDesignName = name;
    this.createPlacementGhostForCombat(design.components);
    this.phaseText.setText('Deploying...').setColor('#44ccff').setVisible(true);
  }

  /** Creates a placement ghost without touching the workshopPanel (used during combat deploy) */
  private createPlacementGhostForCombat(components: { type: ComponentType; lx: number; ly: number }[]): void {
    if (this.placementGhost) { this.placementGhost.destroy(); this.placementGhost = null; }
    const ghost = this.add.container(0, 0).setDepth(50).setAlpha(0.55);
    const preview = drawRobotPreview(this, components, 50);
    ghost.add(preview);
    const ring = this.add.circle(0, 0, TILE_SIZE / 2, 0x44ff44, 0.12)
      .setStrokeStyle(1, 0x44ff44, 0.4);
    ghost.addAt(ring, 0);
    this.placementGhost = ghost;

    // Show anchor zones for mobile/flying
    this.clearAnchorZones();
    const isMobile = components.some(c => c.type === ComponentType.WHEEL || c.type === ComponentType.WING || c.type === ComponentType.TURBINE);
    const hasStabLegs = components.some(c => c.type === ComponentType.STAB_LEGS);
    if (isMobile || hasStabLegs) {
      this.showAnchorZones();
    }

    // Slide combat deploy panel out while placing
    this.tweens.killTweensOf(this.combatDeployPanel);
    this.tweens.add({
      targets: this.combatDeployPanel,
      x: -150,
      duration: 150,
      ease: 'Power2',
    });
    this.cancelPlacingBtn.setVisible(true);
  }

  private createPresetButton(preset: RobotPreset, x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.rectangle(0, 0, 124, 54, 0x222244, 1);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x444488);
    bg.setInteractive({ useHandCursor: true });
    container.add(bg);

    // Robot preview thumbnail (left side, added after bg so it renders on top)
    const preview = drawRobotPreview(this, preset.components, 36);
    preview.setPosition(24, 27);
    container.add(preview);

    const nameText = this.add.text(48, 4, preset.name, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    });

    const costText = this.add.text(48, 18, `$${displayCost(presetCost(preset))}`, {
      fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace',
    });
    this.presetCostTexts.push(costText);

    container.add([nameText, costText]);

    bg.on('pointerover', () => bg.setFillStyle(0x333366));
    bg.on('pointerout', () => bg.setFillStyle(0x222244));
    bg.on('pointerdown', () => { SoundManager.play('clickSelect'); this.selectPreset(preset, container); });

    return container;
  }

  /** Small sell icon — shown in world space above the selected robot */
  private createSellIcon(): void {
    this.robotInspector.createSellIcon();
    this.sellIcon = this.robotInspector.sellIcon;
  }

  /** Cancel icon — full refund for a just-placed robot */
  private createCancelIcon(): void {
    this.robotInspector.createCancelIcon();
    this.cancelIcon = this.robotInspector.cancelIcon;
  }

  /** Small repair icon — shown in world space above the selected robot (left of SELL) */
  private createRepairIcon(): void {
    this.robotInspector.createRepairIcon();
    this.repairIcon = this.robotInspector.repairIcon;
  }

  /** Rebuild icon — shown when robot has ejected components (full factory reset) */
  private createRebuildIcon(): void {
    this.robotInspector.createRebuildIcon();
    this.rebuildIcon = this.robotInspector.rebuildIcon;
  }

  /** Modify icon — opens workshop editor for the selected deployed robot */
  private createModifyIcon(): void {
    this.robotInspector.createModifyIcon();
    this.modifyIcon = this.robotInspector.modifyIcon;
  }

  // ── Base Inspector Panel (right side, shown when clicking player base) ──

  private createBaseInspectorPanel(): void {
    this.robotInspector.createBaseInspectorPanel();
    this.baseInspectorPanel = this.robotInspector.baseInspectorPanel;
  }

  private showBaseInspector(): void {
    this.robotInspector.showBaseInspector();
  }

  private hideBaseInspector(): void {
    this.robotInspector.hideBaseInspector();
  }

  /** Animate HUD HP bar growing after base upgrade (old→new max, restore to full) */
  private animateBaseHpBarUpgrade(
    _oldHp: number, _oldMaxHp: number,
    _newHp: number, _newMaxHp: number,
  ): void {
    // Rebuild segments for new maxHp, then flash white → normal color
    const barX = this.baseHpBarBg.x;
    const barY = this.baseHpBarBg.y;
    const barW = this.baseHpBarBg.width;
    const barH = this.baseHpBarBg.height;
    this.buildBaseHpSegments(barX, barY, barW, barH);

    // Flash all filled segments white briefly
    const filled = this.baseHpSegments.filter(s => s.alpha === 1);
    for (const seg of filled) seg.setFillStyle(0xffffff);
    this.time.delayedCall(400, () => this.updateBaseHpSegments());

    // Pulse effect on the HUD bar background
    const oldBarBgAlpha = this.baseHpBarBg.alpha;
    this.tweens.add({
      targets: this.baseHpBarBg,
      alpha: 0.3,
      duration: 150,
      yoyo: true,
      repeat: 2,
      onComplete: () => { this.baseHpBarBg.setAlpha(oldBarBgAlpha); },
    });
  }

  /** Show sell & repair icons above the selected robot (workshop phase only) */
  private showSellIcon(robot: Robot): void {
    this.robotInspector.showSellIcon(robot);
  }

  // ── Cinematic visibility handlers ──

  private onCinematicStarted = (): void => {
    this.startButton?.setVisible(false);
  };

  private onCinematicEnded = (): void => {
    if (this.phase === GamePhase.WORKSHOP) {
      this.startButton?.setVisible(true);
    }
  };

  // ── Cinematic action handlers (bypass phase/cinematicActive guards) ──

  private onCinematicSelectPreset = (data: { presetName: string }): void => {
    const preset = PRESETS.find(p => p.name === data.presetName);
    if (!preset) {
      console.warn(`[UIScene] cinematic:selectPreset — unknown preset "${data.presetName}"`);
      return;
    }
    this.cancelPlacement();
    this.placementPreset = preset;
    this.createPlacementGhost(preset.components);
  };

  private onCinematicPlaceRobot = (data: { col: number; row: number; presetName: string }): void => {
    const preset = PRESETS.find(p => p.name === data.presetName) ?? this.placementPreset;
    if (!preset) {
      console.warn(`[UIScene] cinematic:autoPlaceRobot — no preset`);
      return;
    }
    this.placementPreset = preset;
    this.placeRobotAt(data.col, data.row);
    this.cancelPlacement();
  };

  // ── Preset selection & placement ──

  private selectPreset(preset: RobotPreset, sourceContainer?: Phaser.GameObjects.Container | null): void {
    if (this.phase !== GamePhase.WORKSHOP && !this.registry.get('cinematicDispatching')) return;
    const cost = displayCost(presetCost(preset));
    if (this.resources < cost) {
      this.showCantAffordFeedback(sourceContainer);
      return;
    }

    // Cancel previous ghost
    this.cancelPlacement();

    this.placementPreset = preset;
    this.createPlacementGhost(preset.components);
    this.phaseText.setText(t('hud.phase.placing')).setColor('#ff8844');
    this.warnIfBudgetTight(cost);
    // Notify tutorial that a preset was selected
    EventBus.emit(GameEvents.TUTORIAL_PRESET_SELECTED);
  }

  /** Create a semi-transparent robot preview that follows the cursor during placement */
  private createPlacementGhost(components: { type: ComponentType; lx: number; ly: number }[]): void {
    if (this.placementGhost) { this.placementGhost.destroy(); this.placementGhost = null; }
    const ghost = this.add.container(0, 0).setDepth(50).setAlpha(0.55);
    const preview = drawRobotPreview(this, components, 50);
    ghost.add(preview);
    // Tile highlight circle behind
    const ring = this.add.circle(0, 0, TILE_SIZE / 2, 0x44ff44, 0.12)
      .setStrokeStyle(1, 0x44ff44, 0.4);
    ghost.addAt(ring, 0);
    this.placementGhost = ghost;

    // Show anchor zone circles for mobile/flying robots
    this.clearAnchorZones();
    const isMobile = components.some(c => c.type === ComponentType.WHEEL || c.type === ComponentType.WING || c.type === ComponentType.TURBINE);
    const hasStabLegs = components.some(c => c.type === ComponentType.STAB_LEGS);
    if (isMobile || hasStabLegs) {
      this.showAnchorZones();
    }

    // Slide workshop panel out of view to free up map space
    this.slideWorkshopPanel(false);
    this.cancelPlacingBtn.setVisible(true);
  }

  private openWorkshop(): void {
    if (this.phase !== GamePhase.WORKSHOP) return;
    this.cancelPlacement();
    // Sync game resources into registry so WorkshopScene can use them in quick play
    if (this.gameScene) this.registry.set('gameResources', this.gameScene.resources);
    // Pause game and UI, launch workshop overlay
    this.scene.pause('GameScene');
    this.scene.pause('UIScene');
    this.scene.launch('WorkshopScene');
  }

  private openWorkshopForEdit(robot: Robot): void {
    if (this.phase !== GamePhase.WORKSHOP) return;
    this.cancelPlacement();
    if (this.gameScene) this.registry.set('gameResources', this.gameScene.resources);
    // Always pass the blueprint (designComponents), never the runtime state (components)
    // Pass raw costs — WorkshopScene will use the same formula as onEditComplete
    const rawOldCost = robot.designCost;
    const modFee = GOD.freeBuilding ? 0 : Math.max(
      GOD.modificationFeeMin,
      Math.round(rawOldCost * GOD.modificationFeePercent),
    );
    const editData: WorkshopEditData = {
      components: robot.designComponents.map(c => ({ ...c })),
      rules: robot.designRules.map(r => ({ ...r })),
      name: robot.designName || robot.id,
      editingRobotId: robot.id,
      modFee,
      originalCost: rawOldCost,
      playerResources: this.resources,
    };
    this.scene.pause('GameScene');
    this.scene.pause('UIScene');
    this.scene.launch('WorkshopScene', editData);
  }

  private deleteRobot(robot: Robot): void {
    this.robotInspector.deleteRobot(robot);
  }

  /** Cancel a just-placed robot — full refund (100%) */
  private cancelRobot(robot: Robot): void {
    this.robotInspector.cancelRobot(robot);
  }

  /** Pay to fully repair a single robot */
  private repairRobot(robot: Robot): void {
    this.robotInspector.repairRobot(robot);
  }

  /** Pay to fully rebuild a robot (restore ejected components + repair all) */
  private rebuildRobot(robot: Robot): void {
    this.robotInspector.rebuildRobot(robot);
  }

  /** Orange floating "REBUILT" text above a rebuilt robot */

  /** Pay to repair ALL damaged robots at once */
  private repairAllRobots(): void {
    this.robotInspector.repairAllRobots();
  }

  /** Green floating "+HP" text above a repaired robot */

  private cancelPlacement(): void {
    const wasPlacing = !!(this.placementGhost);
    if (this.placementGhost) {
      this.placementGhost.destroy();
      this.placementGhost = null;
    }
    this.placementPreset = null;
    this.placementDesign = null;
    this.placementDesignName = '';
    this.fundFailStreak = 0;
    this.clearAnchorZones();
    if (this.phase === GamePhase.WORKSHOP) {
      this.phaseText.setText(t('hud.phase.workshop')).setColor('#44ff44').setVisible(true);
    } else {
      this.phaseText.setVisible(false);
    }
    // Slide appropriate panel back in after placement
    if (wasPlacing) {
      if (this.phase === GamePhase.WORKSHOP) {
        this.slideWorkshopPanel(true);
      } else {
        // During combat: re-show combat deploy panel so player can deploy more
        this.showCombatDeployPanel();
      }
      this.cancelPlacingBtn.setVisible(false);
    }
  }

  /** Show deployment zone circles around alive fixed robots (anchors) and player bases. */
  private showAnchorZones(): void {
    this.clearAnchorZones();
    const gameCam = this.gameScene?.cameras?.main;
    if (!gameCam) return;
    for (const r of this.robotManager.robots) {
      if (!r.alive || r.canFly() || r.moveForce > 0) continue;
      const anchorRange = Math.max(r.sensorRange, GOD.eyesightRange);
      if (anchorRange <= 0) continue;
      const circle = this.gameScene!.add.circle(r.x, r.y, anchorRange, 0x44ff44, 0.06)
        .setStrokeStyle(1, 0x44ff44, 0.25).setDepth(5);
      this.anchorZoneCircles.push(circle);
    }
    // Show base deployment zones
    const baseRange = GOD.baseDeployRange * TILE_SIZE;
    if (baseRange > 0) {
      for (const bp of BASE_POSITIONS) {
        const circle = this.gameScene!.add.circle(bp.x, bp.y, baseRange, 0x4488ff, 0.06)
          .setStrokeStyle(1, 0x4488ff, 0.25).setDepth(5);
        this.anchorZoneCircles.push(circle);
      }
    }
  }

  /** Remove all anchor zone circles. */
  private clearAnchorZones(): void {
    for (const c of this.anchorZoneCircles) c.destroy();
    this.anchorZoneCircles.length = 0;
  }

  /** Slide the workshop panel in (visible) or out (off-screen left) */
  private slideWorkshopPanel(show: boolean): void {
    const targetX = show ? 0 : -150;
    this.tweens.killTweensOf(this.workshopPanel);
    this.tweens.add({
      targets: this.workshopPanel,
      x: targetX,
      duration: 200,
      ease: show ? 'Back.easeOut' : 'Power2',
    });
  }

  /** Create the "CANCEL PLACING" button (hidden by default, shown during placement) */
  private createCancelPlacingButton(): void {
    this.cancelPlacingBtn = createSmallTechButton(
      this, 8 + 65, CANVAS_HEIGHT - 30,
      '✕ CANCEL', 0xcc4444,
      () => { SoundManager.play('click'); this.cancelPlacement(); },
      200,
    );
    this.cancelPlacingBtn.setVisible(false);
  }

  // ── "Can't afford" multi-feedback ──

  /**
   * Rich feedback when the player tries to select/place a design they can't afford.
   * Combines: error sound, camera shake, money text flash, floating popup,
   * and an optional source container flash (preset button / saved design row).
   */
  /** Compare two component arrays to detect actual design changes */
  private hasDesignChanged(
    oldComps: RobotDesign['components'],
    newComps: RobotDesign['components'],
  ): boolean {
    if (oldComps.length !== newComps.length) return true;
    // Sort both by position for stable comparison
    const sort = (a: RobotDesign['components'][0], b: RobotDesign['components'][0]) =>
      a.lx - b.lx || a.ly - b.ly;
    const sortedOld = [...oldComps].sort(sort);
    const sortedNew = [...newComps].sort(sort);
    for (let i = 0; i < sortedOld.length; i++) {
      const o = sortedOld[i];
      const n = sortedNew[i];
      if (o.type !== n.type || o.lx !== n.lx || o.ly !== n.ly
        || (o.rotation ?? 0) !== (n.rotation ?? 0)
        || (o.evoLevel ?? 0) !== (n.evoLevel ?? 0)
        || !!(o.flipX) !== !!(n.flipX)
        || !!(o.flipY) !== !!(n.flipY)) {
        return true;
      }
    }
    return false;
  }

  private showCantAffordFeedback(sourceContainer?: Phaser.GameObjects.Container | null): void {
    // 1. Error sound
    SoundManager.play('error');

    // 2. Camera micro-shake (vibration)
    this.cameras.main.shake(220, 0.006);

    // 3. Money text blink red 3×
    if (this.resourcesText) {
      const rt = this.resourcesText;
      const origColor = '#ffcc00';
      let blinks = 0;
      const blinkTimer = this.time.addEvent({
        delay: 120,
        repeat: 5,
        callback: () => {
          rt.setColor(blinks % 2 === 0 ? '#ff2222' : origColor);
          rt.setScale(blinks % 2 === 0 ? 1.25 : 1.0);
          blinks++;
          if (blinks > 5) {
            rt.setColor(origColor).setScale(1.0);
            blinkTimer.destroy();
          }
        },
      });
    }

    // 4. Floating popup at screen center
    const cx = this.cameras.main.width / 2;
    const cy = this.cameras.main.height * 0.35;
    const msg = t('workshop.notEnoughMoney');
    const popup = this.add.text(cx, cy, msg, {
      fontSize: '18px', fontFamily: 'monospace', fontStyle: 'bold',
      color: '#ff4444', stroke: '#000000', strokeThickness: 4,
      align: 'center',
    }).setOrigin(0.5).setDepth(600);
    this.tweens.add({
      targets: popup,
      y: cy - 40,
      alpha: 0,
      duration: 1200,
      ease: 'Cubic.easeOut',
      onComplete: () => popup.destroy(),
    });

    // 5. Source container flash red (preset button / saved design row)
    if (sourceContainer && sourceContainer.active) {
      const children = sourceContainer.getAll();
      // tint all sprites/images/text to red briefly
      for (const child of children) {
        if ('setTint' in child && typeof (child as any).setTint === 'function') {
          (child as any).setTint(0xff4444);
        }
      }
      this.time.delayedCall(350, () => {
        for (const child of children) {
          if ('clearTint' in child && typeof (child as any).clearTint === 'function') {
            (child as any).clearTint();
          }
        }
      });
    }

    // 6. If placement ghost is active, flash it red
    if (this.placementGhost) {
      const ghost = this.placementGhost;
      ghost.setAlpha(0.8);
      const ring = ghost.getAt(0) as Phaser.GameObjects.Arc;
      if (ring && 'setFillStyle' in ring) {
        ring.setFillStyle(0xff2222, 0.3);
        ring.setStrokeStyle(2, 0xff2222, 0.8);
        this.time.delayedCall(400, () => {
          if (ghost.active) {
            ring.setFillStyle(0x44ff44, 0.12);
            ring.setStrokeStyle(1, 0x44ff44, 0.4);
            ghost.setAlpha(0.55);
          }
        });
      }
    }
  }

  /** Warn the player if placing this unit will leave them with insufficient funds for any further build */
  private warnIfBudgetTight(cost: number): void {
    const remaining = this.resources - cost;
    if (remaining > 0) return; // can still afford something after placement

    const msg = remaining === 0
      ? t('placement.lastUnit')
      : t('placement.budgetWarning');

    const txt = this.add.text(CANVAS_WIDTH / 2, 60, msg, {
      fontSize: '13px', fontFamily: 'monospace', fontStyle: 'bold',
      color: '#ffaa00', stroke: '#000000', strokeThickness: 3,
      align: 'center',
    }).setOrigin(0.5).setDepth(600);

    this.tweens.add({
      targets: txt,
      alpha: 0,
      duration: 3000,
      delay: 1500,
      ease: 'Power2',
      onComplete: () => txt.destroy(),
    });
  }

  // ── Placement error feedback (escalating sass) ──

  private static get FUNDS_TIERS(): string[][] {
    return [
      // Tier 0: polite (attempts 1-3)
      [t('funds.0.0'), t('funds.0.1'), t('funds.0.2'), t('funds.0.3')],
      // Tier 1: sarcastic (attempts 4-6)
      [t('funds.1.0'), t('funds.1.1'), t('funds.1.2'), t('funds.1.3'), t('funds.1.4')],
      // Tier 2: mocking (attempts 7-10)
      [t('funds.2.0'), t('funds.2.1'), t('funds.2.2'), t('funds.2.3'), t('funds.2.4'), t('funds.2.5')],
      // Tier 3: aggressive (attempts 11-15)
      [t('funds.3.0'), t('funds.3.1'), t('funds.3.2'), t('funds.3.3'), t('funds.3.4'), t('funds.3.5'), t('funds.3.6')],
      // Tier 4: existential (16+)
      [t('funds.4.0'), t('funds.4.1'), t('funds.4.2'), t('funds.4.3'), t('funds.4.4'), t('funds.4.5'), t('funds.4.6'), t('funds.4.7')],
    ];
  }

  private fundFailStreak = 0;

  private showPlacementError(screenX: number, screenY: number, reason: string): void {
    SoundManager.play('error');

    let msg: string;
    if (reason === 'funds') {
      this.fundFailStreak++;
      // Reuse shared shake + money flash (no duplicate sound/popup — those are handled below)
      this.cameras.main.shake(220, 0.006);
      if (this.resourcesText) {
        const rt = this.resourcesText;
        const origColor = '#ffcc00';
        let blinks = 0;
        const blinkTimer = this.time.addEvent({
          delay: 120, repeat: 5,
          callback: () => {
            rt.setColor(blinks % 2 === 0 ? '#ff2222' : origColor);
            rt.setScale(blinks % 2 === 0 ? 1.25 : 1.0);
            blinks++;
            if (blinks > 5) { rt.setColor(origColor).setScale(1.0); blinkTimer.destroy(); }
          },
        });
      }
      const tiers = UIScene.FUNDS_TIERS;
      const tierIdx = this.fundFailStreak <= 3 ? 0
        : this.fundFailStreak <= 6 ? 1
        : this.fundFailStreak <= 10 ? 2
        : this.fundFailStreak <= 15 ? 3 : 4;
      const pool = tiers[tierIdx];
      msg = pool[Math.floor(Math.random() * pool.length)];
    } else if (reason === 'occupied') {
      msg = t('placement.spotTaken');
    } else if (reason === 'no_anchor') {
      msg = t('placement.noAnchor');
    } else {
      return; // 'invalid' tile → no popup, ghost already shows red
    }

    // Scale up font with frustration
    const fontSize = Math.min(22, 14 + this.fundFailStreak * 0.5);

    const txt = this.add.text(screenX, screenY, msg, {
      fontSize: `${fontSize}px`, fontFamily: 'monospace', fontStyle: 'bold',
      color: '#ff4444', stroke: '#000000', strokeThickness: 3,
      align: 'center',
    }).setOrigin(0.5).setDepth(600);

    this.tweens.add({
      targets: txt,
      y: screenY - 50,
      alpha: 0,
      duration: 1400 * GOD.popupDurationMult,
      ease: 'Cubic.easeOut',
      onComplete: () => txt.destroy(),
    });
  }

  /** Active cracked-tile repair popup (only one at a time) */
  private crackedRepairPopup: Phaser.GameObjects.Container | null = null;
  /** Guard: skip one placement tick after a tile repair to prevent auto-placing */
  private repairJustDone = false;

  /** Show a floating prompt to repair a cracked tile */
  private showCrackedTileRepairPrompt(col: number, row: number, screenX: number, screenY: number): void {
    if (!this.gameScene) return;
    const ctm = this.gameScene.crackedTiles;
    if (!ctm || !ctm.isCracked(col, row)) return;

    // Only allow repair during workshop/intermission
    if (this.gameScene.phase === GamePhase.WAVE) {
      const txt = this.add.text(screenX, screenY, t('placement.waitIntermission'), {
        fontSize: '10px', fontFamily: 'monospace', fontStyle: 'bold',
        color: '#ffaa44', stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(600);
      this.tweens.add({
        targets: txt, y: screenY - 40, alpha: 0,
        duration: 1200, ease: 'Cubic.easeOut', onComplete: () => txt.destroy(),
      });
      return;
    }

    // Destroy previous popup
    this.crackedRepairPopup?.destroy();
    this.crackedRepairPopup = null;

    const rawCost = ctm.getRepairCost(col, row);
    const cost = GOD.freeBuilding ? 0 : rawCost;
    const canAfford = this.resources >= cost;
    const tierBlocked = !ctm.canRepairTile(col, row);

    const popup = this.add.container(screenX, screenY - 30).setDepth(600);
    this.crackedRepairPopup = popup;

    // Background
    const bg = this.add.graphics();
    const boxW = 160;
    const boxH = tierBlocked ? 68 : 50;
    fillTechRect(bg, -boxW / 2, -20, boxW, boxH, 0x0d0d1a, 0.95);
    drawTechRect(bg, -boxW / 2, -20, boxW, boxH, tierBlocked ? 0xffaa00 : (canAfford ? 0x44aa44 : 0xff4444), 0.6);
    popup.add(bg);

    // Cost label
    const label = this.add.text(0, -12, t('tile.repairCost', { cost }), {
      fontSize: '10px', fontFamily: 'monospace', fontStyle: 'bold',
      color: tierBlocked ? '#ffaa00' : (canAfford ? '#44ff44' : '#ff4444'),
    }).setOrigin(0.5);
    popup.add(label);

    if (tierBlocked) {
      const warn = this.add.text(0, 6, t('tile.unlockFirst'), {
        fontSize: '8px', fontFamily: 'monospace', color: '#ffaa00',
        wordWrap: { width: boxW - 16 },
        align: 'center',
      }).setOrigin(0.5, 0);
      popup.add(warn);
    } else if (canAfford) {
      const btn = createButton(this, 0, 12, t('tile.repair'), {
        onClick: () => {
          if (this.gameScene!.spendResources(cost)) {
            ctm.repairTile(col, row);
            SoundManager.play('click');
            // Prevent the same click from placing a robot on the freshly-repaired tile
            this.repairJustDone = true;
            this.time.delayedCall(0, () => { this.repairJustDone = false; });
            // Success feedback
            const ok = this.add.text(screenX, screenY - 20, t('tile.repaired'), {
              fontSize: '12px', fontFamily: 'monospace', fontStyle: 'bold',
              color: '#44ff44', stroke: '#000000', strokeThickness: 2,
            }).setOrigin(0.5).setDepth(600);
            this.tweens.add({
              targets: ok, y: screenY - 60, alpha: 0,
              duration: 1000, ease: 'Cubic.easeOut', onComplete: () => ok.destroy(),
            });
          }
          popup.destroy();
          this.crackedRepairPopup = null;
        },
        width: 70, height: 18, fontSize: '9px', color: TECH.ACCENT, depth: 601,
      });
      popup.add(btn);
    } else {
      const noFunds = this.add.text(0, 12, t('workshop.notEnoughMoney'), {
        fontSize: '8px', fontFamily: 'monospace', color: '#ff6666',
      }).setOrigin(0.5);
      popup.add(noFunds);
    }

    // Auto-dismiss after 3s or on next click
    this.time.delayedCall(3000, () => {
      if (this.crackedRepairPopup === popup) {
        popup.destroy();
        this.crackedRepairPopup = null;
      }
    });
  }

  private placeRobotAt(col: number, row: number): string | undefined {
    if (!this.gameScene || !this.robotManager) return;
    // Determine robot type from components
    let components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[] = [];
    let rules: BehaviorRule[] = [];
    let cost = 0;

    if (this.placementPreset) {
      components = this.placementPreset.components;
      rules = [...this.placementPreset.rules];
      cost = presetCost(this.placementPreset);
    } else if (this.placementDesign) {
      components = this.placementDesign.components;
      rules = [...this.placementDesign.rules];
      cost = this.placementDesign.cost;
    } else {
      return;
    }

    // Check tile validity based on robot mobility type
    const hasWings = components.some(c => c.type === ComponentType.WING || c.type === ComponentType.TURBINE);
    const hasWheels = components.some(c => c.type === ComponentType.WHEEL);
    const hasStabLegs = components.some(c => c.type === ComponentType.STAB_LEGS);
    // Bottom jumpers = hop locomotion (mobile). A jumper is "bottom" if it's in the lowest rows.
    const jumpers = components.filter(c => c.type === ComponentType.JUMPER);
    let hasBottomJumper = false;
    if (jumpers.length > 0) {
      const maxY = Math.max(...components.map(c => c.ly));
      hasBottomJumper = jumpers.some(c => c.ly >= maxY - 1);
    }
    // Anchor check helper: is this tile within base range or a fixed robot's sensor range?
    const cx = col * TILE_SIZE + TILE_SIZE / 2;
    const cy = row * TILE_SIZE + TILE_SIZE / 2;
    const isInAnchorRange = (): boolean => {
      const nearRobotAnchor = this.robotManager.robots.some(r => {
        if (!r.alive || r.canFly() || r.moveForce > 0) return false;
        const dx = r.x - cx, dy = r.y - cy;
        const anchorRange = Math.max(r.sensorRange, GOD.eyesightRange);
        return Math.sqrt(dx * dx + dy * dy) <= anchorRange;
      });
      if (nearRobotAnchor) return true;
      const baseRange = GOD.baseDeployRange * TILE_SIZE;
      return baseRange > 0 && BASE_POSITIONS.some(bp => {
        const dx = bp.x - cx, dy = bp.y - cy;
        return Math.sqrt(dx * dx + dy * dy) <= baseRange;
      });
    };

    let tileValid = false;
    if (hasWings) {
      tileValid = this.gameScene.isFlyingPlacementTile(col, row);
    } else if (hasStabLegs) {
      // Stab legs: fixed tiles always OK; mobile tiles allowed if within anchor range
      if (this.gameScene.isFixedPlacementTile(col, row)) {
        tileValid = true;
      } else {
        tileValid = this.gameScene.isMobilePlacementTile(col, row) && isInAnchorRange();
      }
    } else if (hasWheels || hasBottomJumper) {
      tileValid = this.gameScene.isMobilePlacementTile(col, row);
    } else {
      tileValid = this.gameScene.isFixedPlacementTile(col, row);
    }
    if (!tileValid) return 'invalid';

    // Anchor rule: mobile/flying robots can only deploy within sensor range of an alive fixed robot OR within base deploy range
    const isMobileOrFlying = hasWheels || hasWings || hasBottomJumper;
    if (isMobileOrFlying) {
      if (!isInAnchorRange()) return 'no_anchor';
    }

    // Prevent placing on a tile with a blocking decoration (obstacle, resource)
    // Flying robots can hover above obstacles
    if (!hasWings) {
      const decor = getDecorAt(col, row);
      if (decor) {
        const effect = DECOR_INTERACTIONS[decor.type];
        if (effect?.interaction === DecorInteraction.BLOCK) return 'blocked';
      }
    }

    // Prevent placing on a tile already occupied by another robot
    const tileX = col * TILE_SIZE + TILE_SIZE / 2;
    const tileY = row * TILE_SIZE + TILE_SIZE / 2;
    const occupied = this.robotManager.robots.some(
      (r) => r.alive && r.x === tileX && r.y === tileY,
    );
    if (occupied) return 'occupied';

    const baseCost = cost; // raw cost before GOD multipliers
    const effectiveCost = displayCost(cost);
    if (!this.gameScene.spendResources(effectiveCost)) return 'funds';

    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;

    // Floating cost popup (red) at placement position
    if (effectiveCost > 0) {
      const cam = this.gameScene.cameras.main;
      const screenX = (x - cam.scrollX) * cam.zoom;
      const screenY = (y - cam.scrollY) * cam.zoom;
      const popup = this.add.text(screenX, screenY - 10, `-$${effectiveCost}`, {
        fontSize: '14px', fontFamily: 'monospace', fontStyle: 'bold',
        color: '#ff4444', stroke: '#220000', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(600);
      this.tweens.add({
        targets: popup, y: popup.y - 30, alpha: 0,
        duration: 1200, ease: 'Power2',
        onComplete: () => popup.destroy(),
      });
    }

    const robot = new Robot(this.gameScene, x, y);
    for (const comp of components) {
      robot.addComponent(comp.type, comp.lx, comp.ly, comp.rotation ?? 0, comp.flipX ?? false, comp.flipY ?? false, comp.evoLevel ?? 0);
    }

    // Set heading BEFORE resetHpEnergy so stability uses correct flight axis
    const dirComp = components.find(c => c.type === ComponentType.DIRECTION);
    if (dirComp) {
      const rot = dirComp.rotation ?? 0;
      robot.heading = rot;
      robot.buildHeading = rot;
      robot.recalcStats(); // recalc stability with correct heading
    }

    robot.resetHpEnergy();
    robot.normalizeSize();
    robot.placed = true;

    // Store design data for later editing (use base cost, not GOD-modified)
    robot.designComponents = [...components];
    robot.designRules = [...rules];
    robot.designCost = baseCost;
    robot.designName = this.placementPreset?.name ?? this.placementDesignName;

    this.robotManager.addRobot(robot, rules);
    this.lastPlacedRobotId = robot.id;
    // Hide sensor overlay if too many robots deployed (perf: avoid GPU overload)
    const aliveCount = this.robotManager.robots.filter(r => r.alive).length;
    if (aliveCount > UIScene.SENSOR_OVERLAY_LIMIT) {
      robot.showSensorCircle = false;
    }
    this.fundFailStreak = 0; // reset sass counter on successful placement
    // Keep placement mode active so the player can place multiple copies with successive clicks
    // (right-click or ESC to cancel)
    return undefined; // success
  }

  // ── Input ──

  private setupInputHandlers(): void {
    // Click on the game area to place robots or select them
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Right-click is handled separately below (cancel placement / deselect)
      // Check both Phaser's API and raw DOM event for macOS trackpad compatibility
      const rawBtn = (pointer.event as MouseEvent)?.button;
      if (pointer.rightButtonDown() || rawBtn === 2 || (pointer.event && (pointer.event as MouseEvent).ctrlKey)) return;
      // Block all map interaction when game is over or wave-clear overlay is showing
      if (this.gameOver || this.waveClearOverlay?.visible) return;
      // Block map interaction during cinematics (except fakeClick dispatches)
      if (this.cinematicBlocked) return;
      const dispatching = !!this.registry.get('cinematicDispatching');
      // Block clicks when any overlay panel is open — prevents click-through to map
      if (!dispatching && (this.godModePanel.isOpen || this.soundPanelOpen || this.pauseMenu?.visible)) return;
      // Tutorial guard removed — replaced by cinematic system
      // Ignore clicks on the workshop panel area (left 140px) unless panel is slid out
      if (this.workshopPanel.x >= 0 && pointer.x < 140) return;
      // Ignore clicks on the combat deploy panel area (left 140px) when visible
      if (this.combatDeployPanel.visible && this.combatDeployPanel.x >= -10 && pointer.x < 140) return;
      // Ignore clicks on the cancel-placing button area
      if (this.cancelPlacingBtn.visible && pointer.x < 140 && pointer.y > CANVAS_HEIGHT - 50) return;

      // Convert screen coords to world coords via the GameScene camera
      const gameCam = this.gameScene?.cameras?.main;
      let worldX = pointer.x;
      let worldY = pointer.y;
      if (gameCam) {
        const wp = gameCam.getWorldPoint(pointer.x, pointer.y);
        worldX = wp.x;
        worldY = wp.y;
      }
      const col = Math.floor(worldX / TILE_SIZE);
      const row = Math.floor(worldY / TILE_SIZE);

      if (this.placementPreset || this.placementDesign) {
        // If clicking a cracked tile while in placement mode, offer repair instead
        if (this.gameScene?.crackedTiles?.isCracked(col, row)) {
          this.showCrackedTileRepairPrompt(col, row, pointer.x, pointer.y);
          return;
        }
        // Guard: don't place on a tile that was just repaired in this same click
        if (this.repairJustDone) return;
        const err = this.placeRobotAt(col, row);
        if (err) this.showPlacementError(pointer.x, pointer.y, err);
      } else {
        // Check if clicking a cracked tile (no placement active)
        if (this.gameScene?.crackedTiles?.isCracked(col, row)) {
          this.showCrackedTileRepairPrompt(col, row, pointer.x, pointer.y);
          return;
        }
        // Close combat deploy panel if clicking on the map (BASE_CLICKED will re-open it if base is hit)
        const combatPanelWasOpen = this.combatDeployPanel.visible;
        if (combatPanelWasOpen) this.hideCombatDeployPanel();
        this.trySelectRobot(worldX, worldY);
        // If a robot was selected, block GameScene camera drag for this click
        if (this.selectedRobot) {
          this.registry.set('uiConsumedClick', true);
          // Clear on next frame so it only blocks this single click
          this.time.delayedCall(0, () => this.registry.set('uiConsumedClick', false));
        }
      }
    });

    // Right-click: track start position to differentiate click vs camera drag
    // On macOS trackpad, 2-finger tap can emit ctrl+left-click instead of right-click
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const rawBtn = (pointer.event as MouseEvent)?.button;
      const isRight = pointer.rightButtonDown() || rawBtn === 2 || !!(pointer.event && (pointer.event as MouseEvent).ctrlKey);
      if (isRight) {
        this.rightClickStart = { x: pointer.x, y: pointer.y };
      }
    });
    // On release: if short click (no drag), cancel placement / deselect
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if ((pointer.rightButtonReleased() || this.rightClickStart) && this.rightClickStart) {
        const dx = pointer.x - this.rightClickStart.x;
        const dy = pointer.y - this.rightClickStart.y;
        if (dx * dx + dy * dy < 25) { // < 5px movement
          if (this.placementGhost) {
            this.cancelPlacement();
          } else {
            // No active placement — close any open panels
            this.hideCombatDeployPanel();
            this.robotInspector.hideAllIcons();
            this.hideBaseInspector();
            this.selectedRobot = null;
            EventBus.emit(GameEvents.ROBOT_SELECTED, null);
          }
        }
        this.rightClickStart = null;
      }
    });

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown-SPACE', () => {
      // Block during cinematics and cinematic editor
      if (this.cinematicBlocked) return;
      if (this.gameScene?.phase === GamePhase.WAVE) {
        this.toggleSpeed();
      } else {
        this.onStartWave();
      }
    });
    this.input.keyboard?.on('keydown-ESC', () => {
      // During cinematic playback: ESC skips the cinematic
      if (this.registry.get('cinematicActive')) {
        EventBus.emit(CinematicEvents.SKIP);
        return;
      }
      // Editor handles its own ESC (editor pauses UIScene, so this won't fire)
      if (this.registry.get('cinematicEditorActive')) return;
      // If GOD mode panel is open, close it first
      if (this.godModePanel.isOpen) {
        this.toggleGodPanel();
        return;
      }
      // If sound panel is open, close it
      if (this.soundPanelOpen) {
        this.soundPanelOpen = false;
        this.soundPanel.setVisible(false);
        return;
      }
      // If wave-clear overlay is showing, dismiss it → deploy
      if (this.waveClearOverlay?.active) {
        this.waveClearAction('deploy');
        return;
      }
      // If placing a unit, cancel placement first
      if (this.placementGhost) {
        this.cancelPlacement();
        return;
      }
      // If base inspector open, close it first
      if (this.baseInspectorPanel.visible) {
        this.hideBaseInspector();
        return;
      }
      // If inspector open, close it first
      if (this.sellIcon.visible || this.cancelIcon.visible) {
        this.sellIcon.setVisible(false);
        this.cancelIcon.setVisible(false);
        this.repairIcon.setVisible(false);
        this.rebuildIcon.setVisible(false);
        this.modifyIcon.setVisible(false);
        this.selectedRobot = null;
        return;
      }
      // Otherwise toggle pause menu
      this.togglePauseMenu();
    });

    // Number keys 1-5 for preset selection
    for (let i = 0; i < PRESETS.length; i++) {
      this.input.keyboard?.on(`keydown-${i + 1}`, () => {
        this.selectPreset(PRESETS[i], this.presetButtons[i]);
      });
    }

    // C key to open custom builder
    this.input.keyboard?.on('keydown-C', (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      this.openWorkshop();
    });
  }

  private trySelectRobot(px: number, py: number): void {
    this.robotInspector.trySelectRobot(px, py);
  }

  // ── Actions ──

  private onStartWave(): void {
    if (this.gameOver) return;
    // Block during cinematics
    if (this.cinematicBlocked) return;
    this.lastPlacedRobotId = null;
    this.cancelPlacement();
    if (!this.gameScene) {
      this.gameScene = this.registry.get('gameScene') as GameScene;
    }
    // Warn if starting with no deployed units — ask confirmation
    if (this.robotManager && this.robotManager.robots.filter(r => r.alive).length === 0) {
      this.showNoRobotConfirm();
      return;
    }
    if (this.gameScene) {
      this.gameScene.startWave();
    }
  }

  /** Confirmation dialog when trying to start wave with 0 robots */
  private noRobotConfirmPanel?: Phaser.GameObjects.Container;

  private showNoRobotConfirm(): void {
    if (this.noRobotConfirmPanel) return;
    SoundManager.play('error');
    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;
    const panel = this.add.container(cx, cy).setDepth(500);
    const bg = this.add.rectangle(0, 0, 260, 80, 0x111122, 0.95).setStrokeStyle(1, 0xff6644);
    const label = this.add.text(0, -20, t('placement.noRobots'), {
      fontSize: '10px', fontFamily: 'monospace', color: '#ffaa44', align: 'center', wordWrap: { width: 240 },
    }).setOrigin(0.5);
    panel.add([bg, label]);

    const yesBtn = createSmallTechButton(this, -50, 22, 'YES', TECH.DANGER, () => {
      SoundManager.play('clickConfirm');
      this.destroyNoRobotConfirm();
      if (this.gameScene) this.gameScene.startWave();
    });
    const noBtn = createSmallTechButton(this, 50, 22, 'CANCEL', TECH.ACCENT, () => {
      SoundManager.play('click');
      this.destroyNoRobotConfirm();
    });
    panel.add([yesBtn, noBtn]);
    this.noRobotConfirmPanel = panel;
  }

  private destroyNoRobotConfirm(): void {
    this.noRobotConfirmPanel?.destroy();
    this.noRobotConfirmPanel = undefined;
  }

  // ── Event listeners ──

  private setupEventListeners(): void {
    // Remove any leftover listeners first (prevents duplicates on scene restart)
    this.removeEventListeners();

    EventBus.on(GameEvents.WAVE_STARTED, this.onWaveStarted, this);
    EventBus.on(GameEvents.WAVE_CELEBRATION, this.onWaveCompleted, this);
    EventBus.on(GameEvents.WAVE_TRANSITION_READY, this.onWaveTransitionReady, this);
    EventBus.on(GameEvents.BASE_DAMAGED, this.onBaseDamaged, this);
    EventBus.on(GameEvents.GAME_OVER, this.onGameOver, this);
    EventBus.on(GameEvents.RESOURCES_CHANGED, this.onResourcesChanged, this);
    EventBus.on(GameEvents.RESOURCES_UPDATED, this.onSpecialResourcesChanged, this);
    EventBus.on(GameEvents.PHASE_CHANGED, this.onPhaseChanged, this);
    EventBus.on(GameEvents.WORKSHOP_DESIGN_COMPLETE, this.onDesignComplete, this);
    EventBus.on(GameEvents.WORKSHOP_EDIT_COMPLETE, this.onEditComplete, this);
    EventBus.on(GameEvents.CPU_REQUEST_WAVE, this.onCpuRequestWave, this);
    EventBus.on(GameEvents.GOD_PARAMS_CHANGED, () => this.refreshGodVisuals(), this);
    EventBus.on(GameEvents.COIN_FLY_TO_HUD, this.onCoinFlyToHud, this);
    EventBus.on(GameEvents.RESOURCE_FLY_TO_HUD, this.onResourceFlyToHud, this);
    EventBus.on(GameEvents.BASE_CLICKED, this.showCombatDeployPanel, this);

    // Cinematic action events — bypass phase/cinematicActive guards
    EventBus.on(CinematicEvents.SELECT_PRESET, this.onCinematicSelectPreset, this);
    EventBus.on(CinematicEvents.AUTO_PLACE_ROBOT, this.onCinematicPlaceRobot, this);

    // Hide NEXT WAVE button during cinematic playback
    EventBus.on(CinematicEvents.START, this.onCinematicStarted, this);
    EventBus.on(CinematicEvents.COMPLETE, this.onCinematicEnded, this);
  }

  private removeEventListeners(): void {
    EventBus.off(GameEvents.WAVE_STARTED, this.onWaveStarted, this);
    EventBus.off(GameEvents.WAVE_CELEBRATION, this.onWaveCompleted, this);
    EventBus.off(GameEvents.WAVE_TRANSITION_READY, this.onWaveTransitionReady, this);
    EventBus.off(GameEvents.BASE_DAMAGED, this.onBaseDamaged, this);
    EventBus.off(GameEvents.GAME_OVER, this.onGameOver, this);
    EventBus.off(GameEvents.RESOURCES_CHANGED, this.onResourcesChanged, this);
    EventBus.off(GameEvents.RESOURCES_UPDATED, this.onSpecialResourcesChanged, this);
    EventBus.off(GameEvents.PHASE_CHANGED, this.onPhaseChanged, this);
    EventBus.off(GameEvents.WORKSHOP_DESIGN_COMPLETE, this.onDesignComplete, this);
    EventBus.off(GameEvents.WORKSHOP_EDIT_COMPLETE, this.onEditComplete, this);
    EventBus.off(GameEvents.CPU_REQUEST_WAVE, this.onCpuRequestWave, this);
    EventBus.off(GameEvents.COIN_FLY_TO_HUD, this.onCoinFlyToHud, this);
    EventBus.off(GameEvents.RESOURCE_FLY_TO_HUD, this.onResourceFlyToHud, this);
    EventBus.off(GameEvents.BASE_CLICKED, this.showCombatDeployPanel, this);
    EventBus.off(CinematicEvents.SELECT_PRESET, this.onCinematicSelectPreset, this);
    EventBus.off(CinematicEvents.AUTO_PLACE_ROBOT, this.onCinematicPlaceRobot, this);
    EventBus.off(CinematicEvents.START, this.onCinematicStarted, this);
    EventBus.off(CinematicEvents.COMPLETE, this.onCinematicEnded, this);
  }

  private onWaveStarted = (waveNum: number): void => {
    this.phaseText.setVisible(false); // redundant with waveText during combat
    this.startButton.setVisible(false);
    this.speedButton.setVisible(true);
    this.workshopPanel.setVisible(false);
    this.waveText.setText(t('hud.waveOf', { n: waveNum, total: this.waveManager?.totalWaves ?? 5 })).setColor('#ff4444');
    this.setSensorCirclesVisible(false);
    this.hideWavePreview();
    // Resume component tweens paused during WORKSHOP
    if (this.robotManager) {
      for (const r of this.robotManager.robots) {
        if (r.alive) r.resumeComponentTweens();
      }

      // ── Easter egg: Pacifist Run — react to weaponless robots ──
      if (GOD.eePacifist && waveNum === 1) {
        for (const r of this.robotManager.robots) {
          if (!r.alive) continue;
          if (r.getWeapons().length === 0 && r.components.length > 0) {
            // Show floating text above the pacifist robot
            const msg = "...you do realize this is a COMBAT game, right?";
            this.time.delayedCall(2000, () => {
              if (!r.alive) return;
              const txt = this.add.text(r.x, r.y - 30, msg, {
                fontSize: '9px', color: '#aaaaff', fontFamily: 'monospace', fontStyle: 'italic',
                stroke: '#000000', strokeThickness: 2, wordWrap: { width: 160 }, align: 'center',
              }).setOrigin(0.5, 1).setDepth(150).setAlpha(0);
              this.tweens.add({
                targets: txt, alpha: 1, y: txt.y - 10,
                duration: 400, ease: 'Back.easeOut',
              });
              this.time.delayedCall(4000, () => {
                this.tweens.add({
                  targets: txt, alpha: 0, duration: 500,
                  onComplete: () => txt.destroy(),
                });
              });
            });
            // Also show occasional "..." bubbles during combat
            this.time.addEvent({
              delay: 8000 + Math.random() * 5000,
              repeat: 3,
              callback: () => {
                if (!r.alive) return;
                const dots = this.add.text(r.x + (Math.random() - 0.5) * 10, r.y - 20, '...', {
                  fontSize: '11px', color: '#888899', fontFamily: 'monospace',
                  stroke: '#000000', strokeThickness: 2,
                }).setOrigin(0.5).setDepth(150).setAlpha(0);
                this.tweens.add({
                  targets: dots, alpha: 0.8, y: dots.y - 15,
                  duration: 600, yoyo: true, hold: 1000,
                  onComplete: () => dots.destroy(),
                });
              },
            });
          }
        }
      }
    }
  };

  private onWaveCompleted = (waveNum: number): void => {
    // Workshop tutorial removed — replaced by cinematic trigger system

    // Reset speed
    this.gameSpeed = 0.5;
    this.speedText.setText(t('hud.speed', { n: 0.5 }));
    if (this.gameScene) this.gameScene.gameSpeed = 0.5;

    // Instant flash text — overlay with buttons comes later (after repair)
    this.showWaveClearedFlash();
  };

  /** Fired after repair sequence (or immediately if no repair needed). Shows the action overlay. */
  private onWaveTransitionReady = (): void => {
    this.showWaveClearOverlay();
  };

  /** CPU bot requests to start the next wave — dismiss overlay and use the normal UI flow */
  private onCpuRequestWave = (): void => {
    this.waveClearAction('nextWave');
  };

  /** Instant "WAVE CLEARED!" flash text shown the moment last enemy dies. */
  private showWaveClearedFlash(): void {
    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2 - 60;
    const flash = this.add.text(cx, cy, t('waveClear.cleared'), {
      fontSize: '36px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#442200', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(600).setScale(0.3).setAlpha(0);

    this.tweens.add({
      targets: flash,
      scaleX: 1, scaleY: 1, alpha: 1,
      duration: 300, ease: 'Back.easeOut',
      onComplete: () => {
        // Hold briefly then fade out
        this.tweens.add({
          targets: flash, alpha: 0, y: cy - 30,
          duration: 800, delay: 600, ease: 'Cubic.easeIn',
          onComplete: () => flash.destroy(),
        });
      },
    });
  }

  /** Full-screen overlay: "WAVE TAMED!" + 3 action buttons */
  private showWaveClearOverlay(): void {
    if (this.waveClearOverlay) this.waveClearOverlay.destroy();

    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;
    this.waveClearOverlay = this.add.container(0, 0).setDepth(500);

    // Dark semi-transparent backdrop
    const backdrop = this.add.rectangle(cx, cy, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.65);
    backdrop.setInteractive(); // block clicks through
    this.waveClearOverlay.add(backdrop);

    // Title: "WAVE TAMED!"
    const title = this.add.text(cx, cy - 130, t('waveClear.tamed'), {
      fontSize: '36px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#442200', strokeThickness: 4,
    }).setOrigin(0.5).setScale(0.3).setAlpha(0);
    this.waveClearOverlay.add(title);

    // Animate title in
    this.tweens.add({
      targets: title,
      scaleX: 1, scaleY: 1, alpha: 1,
      duration: 400, ease: 'Back.easeOut',
    });

    // Subtitle with wave info
    const waveNum = this.waveManager?.currentWave ?? 0;
    const sub = this.add.text(cx, cy - 80, t('waveClear.waveCompleted', { n: waveNum, total: this.waveManager?.totalWaves ?? 5 }), {
      fontSize: '16px', color: '#aaaacc', fontFamily: 'monospace',
    }).setOrigin(0.5).setAlpha(0);
    this.waveClearOverlay.add(sub);
    this.tweens.add({ targets: sub, alpha: 1, duration: 300, delay: 300 });

    // Buttons appear after title animation
    const btnDefs = [
      { label: t('waveClear.workshop'), desc: t('waveClear.workshopDesc'), key: 'SHIFT', action: () => this.waveClearAction('workshop') },
      { label: t('waveClear.deploy'), desc: t('waveClear.deployDesc'), key: 'ENTER', action: () => this.waveClearAction('deploy') },
      { label: t('waveClear.nextWave'), desc: t('waveClear.nextWaveDesc'), key: 'SPACE', action: () => this.waveClearAction('nextWave') },
    ];

    const btnW = 340;
    const btnH = 60;
    const btnGap = 18;
    const startY = cy + 10;

    btnDefs.forEach((def, i) => {
      const by = startY + i * (btnH + btnGap);
      const btnContainer = createButton(this, cx, by, def.label, {
        onClick: () => { SoundManager.play('clickConfirm'); def.action(); },
        description: def.desc,
        width: btnW, height: btnH,
        fontSize: '18px',
      });
      btnContainer.setAlpha(0);
      this.waveClearOverlay.add(btnContainer);

      // Staggered fade-in
      this.tweens.add({
        targets: btnContainer,
        alpha: 1, y: by,
        duration: 300, delay: 500 + i * 120,
        ease: 'Quad.easeOut',
      });
    });

    // Keyboard shortcuts (only active while overlay is visible)
    const doAction = (action: 'workshop' | 'deploy' | 'nextWave') => {
      if (!this.waveClearOverlay?.active) return;
      SoundManager.play('clickConfirm');
      this.waveClearAction(action);
    };
    this.input.keyboard?.on('keydown-SHIFT', () => doAction('workshop'));
    this.input.keyboard?.on('keydown-ENTER', () => doAction('deploy'));
    this.input.keyboard?.on('keydown-SPACE', () => doAction('nextWave'));
  }

  /** Show/refresh the persistent wave preview in the top HUD area (below the bar). */
  private refreshWavePreview(): void {
    // Destroy previous
    this.wavePreviewContainer?.destroy();
    this.wavePreviewContainer = null;

    const entries = this.waveManager?.getNextWaveEntries() ?? [];
    if (entries.length === 0) return;

    this.wavePreviewContainer = this.add.container(0, 0).setDepth(100);
    this.addWavePreviewRow(this.wavePreviewContainer, CANVAS_WIDTH / 2, 92, entries, 0);
    // Make it immediately visible (no delay)
    this.wavePreviewContainer.setAlpha(1);
    this.wavePreviewContainer.iterate((child: any) => { if (child.setAlpha) child.setAlpha(1); });
    // Dismiss on ANY interaction (click anywhere, or key press)
    const dismissPreview = () => {
      this.wavePreviewContainer?.setVisible(false);
      this.input.off('pointerdown', dismissPreview);
      this.input.keyboard?.off('keydown', dismissPreview);
    };
    this.input.on('pointerdown', dismissPreview);
    this.input.keyboard?.on('keydown', dismissPreview);
  }

  /** Hide the persistent wave preview (during WAVE phase). */
  private hideWavePreview(): void {
    this.wavePreviewContainer?.setVisible(false);
  }

  /**
   * Add a row of enemy type icons with count badges to a container.
   * Groups entries by type, shows sprite + "×N" for each.
   */
  private addWavePreviewRow(
    parent: Phaser.GameObjects.Container,
    cx: number, y: number,
    entries: WaveEntry[],
    fadeDelay: number,
  ): void {
    // Aggregate by enemy type
    const counts = new Map<EnemyType, number>();
    for (const e of entries) counts.set(e.type, (counts.get(e.type) ?? 0) + 1);
    const types = [...counts.entries()]; // [EnemyType, count][]

    const iconH = 64;                       // portrait enemy sprites (2:3 ratio)
    const iconW = Math.round(iconH * 0.67); // width respects aspect ratio
    const gap = 14;
    const totalW = types.length * iconW + (types.length - 1) * gap;
    const startX = cx - totalW / 2 + iconW / 2;

    // Metal plate background (adapts to list width + total badge)
    const platePad = 16;
    const totalBadgeW = 40; // extra space for TOTAL label + number
    const plateW = Math.max(160, totalW + platePad * 2 + totalBadgeW + 10);
    const plateH = 130;
    const plateX = cx - plateW / 2;
    const plateY = y - 30;
    const plateBg = this.add.graphics();
    drawMetalPlate(plateBg, plateX, plateY, plateW, plateH, 0x334455, { bevel: 2, alpha: 0.9 });
    // Inner subtle border
    plateBg.lineStyle(1, 0x8899aa, 0.3);
    plateBg.strokeRect(plateX + 3, plateY + 3, plateW - 6, plateH - 6);
    parent.add(plateBg);

    // (dismiss handled by scene-wide pointerdown/keydown in refreshWavePreview)

    // Label
    const label = this.add.text(cx, y - 18, t('waveClear.nextWave'), {
      fontSize: '10px', color: '#aabbcc', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0);
    parent.add(label);

    const iconContainer = this.add.container(0, 0).setAlpha(0);
    parent.add(iconContainer);

    types.forEach(([eType, count], i) => {
      const ix = startX + i * (iconW + gap);
      const texKey = ENEMY_TEXTURES[eType];
      const stat = ENEMY_STATS[eType];

      // Enemy sprite icon — fit within box preserving aspect ratio
      const iy = y + 30;
      if (this.textures.exists(texKey)) {
        const icon = this.add.image(ix, iy, texKey);
        const srcW = icon.width || 1;
        const srcH = icon.height || 1;
        const scale = Math.min(iconW / srcW, iconH / srcH);
        icon.setScale(scale);
        iconContainer.add(icon);
      } else {
        // Fallback: colored circle
        const circle = this.add.circle(ix, iy, iconW / 2, stat?.color ?? 0xff4444, 0.8);
        iconContainer.add(circle);
      }

      // Count badge
      if (count > 1) {
        const badge = this.add.text(ix + iconW / 2 - 2, iy + iconH / 2 - 4, `×${count}`, {
          fontSize: '10px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
          stroke: '#000000', strokeThickness: 2,
        }).setOrigin(1, 1);
        iconContainer.add(badge);
      }

      // Type label below icon
      const typeName = stat?.label ?? eType;
      const nameText = this.add.text(ix, iy + iconH / 2 + 4, String(typeName), {
        fontSize: '7px', color: '#aabbbb', fontFamily: 'monospace',
      }).setOrigin(0.5, 0);
      iconContainer.add(nameText);
    });

    // Total count (right side of plate) — "TOTAL" label + number
    const totalX = cx + totalW / 2 + 25;
    const totalLabel = this.add.text(totalX, y + 15, t('waveClear.total'), {
      fontSize: '7px', color: '#8899aa', fontFamily: 'monospace',
    }).setOrigin(0.5, 0.5).setAlpha(0);
    parent.add(totalLabel);
    const totalText = this.add.text(totalX, y + 29, `${entries.length}`, {
      fontSize: '13px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5).setAlpha(0);
    parent.add(totalText);

    // Animate in
    this.tweens.add({ targets: plateBg, alpha: { from: 0, to: 1 }, duration: 250, delay: fadeDelay });
    this.tweens.add({ targets: label, alpha: 1, duration: 300, delay: fadeDelay });
    this.tweens.add({ targets: iconContainer, alpha: 1, duration: 400, delay: fadeDelay + 100 });
    this.tweens.add({ targets: [totalLabel, totalText], alpha: 1, duration: 300, delay: fadeDelay + 200 });
  }

  /** Handle wave-clear overlay button actions */
  private waveClearAction(action: 'workshop' | 'deploy' | 'nextWave'): void {
    // Dismiss overlay
    if (this.waveClearOverlay) {
      this.tweens.add({
        targets: this.waveClearOverlay,
        alpha: 0, duration: 200,
        onComplete: () => {
          this.waveClearOverlay?.destroy();
          // Show wave preview now that overlay is gone
          if (action !== 'nextWave') this.refreshWavePreview();
        },
      });
    }

    // Common: transition to workshop phase
    const enterWorkshop = (): void => {
      this.phaseText.setText(t('hud.phase.workshop')).setColor('#44ff44').setVisible(true);
      this.waveText.setColor('#ffffff');
      this.startButton.setVisible(!this.registry.get('cinematicActive'));
      this.speedButton.setVisible(false);
      this.workshopPanel.setVisible(true);
      // Hide combat deploy panel when returning to workshop
      if (this.combatDeployPanel.visible) this.hideCombatDeployPanel();
      this.cancelPlacement();
      this.setSensorCirclesVisible(true);
      // Pause component tweens to save CPU during WORKSHOP (no visual need)
      if (this.robotManager) {
        for (const r of this.robotManager.robots) {
          if (r.alive) r.pauseComponentTweens();
        }
      }
    };

    switch (action) {
      case 'workshop':
        enterWorkshop();
        // Open WorkshopScene for design creation (same pattern as openWorkshop)
        this.time.delayedCall(250, () => {
          if (this.gameScene) this.registry.set('gameResources', this.gameScene.resources);
          this.scene.pause('GameScene');
          this.scene.pause('UIScene');
          this.scene.launch('WorkshopScene');
        });
        break;
      case 'deploy':
        enterWorkshop();
        break;
      case 'nextWave':
        // Skip workshop, start next wave immediately — counts as wave chaining for scoring
        EventBus.emit(GameEvents.WAVE_CHAINED);
        this.time.delayedCall(250, () => this.onStartWave());
        break;
    }
  }

  /** Build or rebuild all segment rectangles for the base HP bar */
  private buildBaseHpSegments(barX: number, barY: number, barW: number, barH: number): void {
    // Clean up old segments
    for (const seg of this.baseHpSegments) seg.destroy();
    this.baseHpSegments = [];
    this.baseHpSegmentBlink?.remove();
    this.baseHpSegmentBlink = undefined;

    const segPct = Math.max(5, GOD.baseHpSegmentPct) / 100;
    const count = Math.max(1, Math.round(1 / segPct));
    const gap = 1; // px gap between segments
    const totalGap = (count - 1) * gap;
    const segW = Math.max(1, (barW - totalGap) / count);

    for (let i = 0; i < count; i++) {
      const sx = barX + i * (segW + gap);
      const seg = this.add.rectangle(sx, barY, segW, barH, 0x44cc44).setOrigin(0, 0).setDepth(102);
      this.baseHpSegments.push(seg);
    }
    this.updateBaseHpSegments();
  }

  /** Color each segment based on current HP ratio; blink red when critical */
  private updateBaseHpSegments(): void {
    const ratio = Math.max(0, this.baseHp / this.baseMaxHp);
    const count = this.baseHpSegments.length;
    const filledCount = Math.ceil(ratio * count);

    // Stop old blink
    this.baseHpSegmentBlink?.remove();
    this.baseHpSegmentBlink = undefined;

    for (let i = 0; i < count; i++) {
      const seg = this.baseHpSegments[i];
      if (i >= filledCount) {
        // Empty segment
        seg.setFillStyle(0x333333);
        seg.setAlpha(0.4);
      } else {
        seg.setAlpha(1);
        // All filled segments share the same color based on overall HP ratio
        if (ratio > 0.6) {
          seg.setFillStyle(0x44cc44); // green
        } else if (ratio > 0.35) {
          seg.setFillStyle(0xcccc44); // yellow
        } else if (ratio > 0.15) {
          seg.setFillStyle(0xff8800); // orange
        } else {
          seg.setFillStyle(0xcc4444); // red
        }
      }
    }

    // Label color
    if (ratio > 0.5) {
      this.baseHpText.setColor('#ffffff');
    } else if (ratio > 0.25) {
      this.baseHpText.setColor('#cccc44');
    } else {
      this.baseHpText.setColor('#ff4444');
    }

    // Blink remaining segments when critical (<25%)
    if (ratio > 0 && ratio <= 0.25) {
      const blinkTargets = this.baseHpSegments.filter((_, i) => i < filledCount);
      if (blinkTargets.length > 0) {
        this.baseHpSegmentBlink = this.tweens.add({
          targets: blinkTargets,
          alpha: { from: 1, to: 0.3 },
          duration: 400,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }
  }

  private onBaseDamaged = (data: { hp: number; x: number; y: number }): void => {
    this.baseHp = data.hp;
    // Re-read maxHp in case base was upgraded
    const pb = this.registry.get('playerBase') as { maxHp: number } | undefined;
    if (pb) this.baseMaxHp = pb.maxHp;

    this.updateBaseHpSegments();

    // Flash effect on damage (skip while GOD panel is open to avoid visual noise)
    if (!this.godModePanel?.isOpen) {
      this.cameras.main.flash(150, 255, 50, 50, false);
    }
  };

  private onResourcesChanged = (amount: number): void => {
    this.resources = amount;
    this.resourcesText?.setText(`${this.resources}`);
    this.repositionHudResources();
  };

  // ── Coin fly-to-HUD animation ──
  private coinFlyQueue = 0;

  private onCoinFlyToHud = (data: { screenX: number; screenY: number; amount: number }): void => {
    const targetX = this.coinIcon.x;
    const targetY = this.coinIcon.y;
    const staggerDelay = this.coinFlyQueue * GOD.coinFlyStaggerDelay;
    this.coinFlyQueue++;

    JuiceEffects.coinFlyToHud(
      this,
      data.screenX, data.screenY,
      targetX, targetY,
      data.amount,
      staggerDelay,
      () => {
        EventBus.emit(GameEvents.COIN_ARRIVED, { amount: data.amount });
        this.pulseHudCoinIcon();
        this.coinFlyQueue = Math.max(0, this.coinFlyQueue - 1);
      },
    );
  };

  private pulseHudCoinIcon(): void {
    if (!this.coinIcon) return;
    // Use the fixed display size as base, not current scale (avoids exponential growth if pulses overlap)
    const ico = 31;
    const baseScaleX = ico / this.coinIcon.width;
    const baseScaleY = ico / this.coinIcon.height;
    this.tweens.killTweensOf(this.coinIcon);
    this.coinIcon.setScale(baseScaleX, baseScaleY);
    this.tweens.add({
      targets: this.coinIcon,
      scaleX: baseScaleX * GOD.coinFlyHudPulseScale,
      scaleY: baseScaleY * GOD.coinFlyHudPulseScale,
      duration: 100,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  // ── Resource (Co/Electro) fly-to-HUD animation ──
  private resourceFlyQueue = 0;

  private onResourceFlyToHud = (data: { screenX: number; screenY: number; amount: number; type: 'co' | 'electro' }): void => {
    const isCo = data.type === 'co';
    const targetIcon = isCo ? this.coIcon : this.electroIcon;
    if (!targetIcon) return;
    const targetX = targetIcon.x;
    const targetY = targetIcon.y;
    const staggerDelay = this.resourceFlyQueue * GOD.coinFlyStaggerDelay;
    this.resourceFlyQueue++;

    JuiceEffects.lootFlyToHud(
      this,
      data.screenX, data.screenY,
      targetX, targetY,
      data.amount,
      staggerDelay,
      () => {
        EventBus.emit(GameEvents.RESOURCE_ARRIVED, { amount: data.amount, type: data.type });
        this.pulseHudIcon(targetIcon);
        this.resourceFlyQueue = Math.max(0, this.resourceFlyQueue - 1);
      },
      isCo ? 'icon_co' : 'icon_electro',
      isCo ? '#44ee88' : '#88ccff',
      'coin', // reuse coin SFX for now
    );
  };

  private pulseHudIcon(icon: Phaser.GameObjects.Image): void {
    if (!icon) return;
    const baseScaleX = icon.scaleX;
    const baseScaleY = icon.scaleY;
    this.tweens.add({
      targets: icon,
      scaleX: baseScaleX * GOD.coinFlyHudPulseScale,
      scaleY: baseScaleY * GOD.coinFlyHudPulseScale,
      duration: 100,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  private onSpecialResourcesChanged = (state: ResourceState): void => {
    this.coText?.setText(`${state.co}`);
    this.electroText?.setText(`${state.electro}`);
    this.repositionHudResources();
  };

  private repositionHudResources(): void {
    if (!this.resourcesText || !this.coIcon || !this.coText || !this.electroIcon || !this.electroText || !this.phaseText) return;
    const ico = 31;
    let hx = this.resourcesText.x + Math.max(40, this.resourcesText.width + 11);
    this.coIcon.setX(hx + ico / 2);
    hx += ico + 4;
    this.coText.setX(hx);
    hx += Math.max(30, this.coText.width + 11);
    this.electroIcon.setX(hx + ico / 2);
    hx += ico + 4;
    this.electroText.setX(hx);
    hx += Math.max(30, this.electroText.width + 11);
    this.phaseText.setX(hx);
  }

  private onPhaseChanged = (phase: GamePhase): void => {
    this.phase = phase;
    if (phase === GamePhase.WORKSHOP || phase === GamePhase.INTERMISSION) {
      // Don't show wave preview while wave-clear overlay is active — it will be shown after dismiss
      if (!this.waveClearOverlay?.active) {
        this.refreshWavePreview();
      }
    }
  };

  private onDesignComplete = (payload: { design: RobotDesign; name: string } | RobotDesign): void => {
    // Support both new format { design, name } and legacy format (just design)
    const design = 'design' in payload ? payload.design : payload;
    const designName = 'name' in payload ? payload.name : '';

    // If we were editing an existing saved design, update it instead of placing
    if (this.editingDesignIndex >= 0) {
      const saved = DesignStore.get(this.editingDesignIndex);
      const name = designName || saved?.name || `Robot-${this.editingDesignIndex}`;
      DesignStore.update(this.editingDesignIndex, name, design);
      // Also remove the duplicate that WorkshopScene just added
      const lastIdx = DesignStore.count() - 1;
      if (lastIdx > this.editingDesignIndex) {
        DesignStore.remove(lastIdx);
      }
      this.editingDesignIndex = -1;
      this.refreshSavedDesigns();
      return;
    }

    const cost = displayCost(design.cost);
    if (this.resources < cost) return;
    this.cancelPlacement();
    this.placementDesign = design;
    this.placementDesignName = designName;
    this.createPlacementGhost(design.components);
    this.phaseText.setText(t('hud.phase.placingCustom')).setColor('#44ccff');
    this.warnIfBudgetTight(cost);
    this.refreshSavedDesigns();
  };

  private onEditComplete = (data: { design: RobotDesign; robotId: string; name: string }): void => {
    if (!this.robotManager || !this.gameScene) return;
    const robot = this.robotManager.robots.find(r => r.id === data.robotId);
    if (!robot || !robot.alive) return;

    // Calculate cost difference
    const oldCost = robot.designCost;
    const newCost = data.design.cost;
    const diff = newCost - oldCost;

    // Detect if actual changes were made (compare components)
    const designChanged = this.hasDesignChanged(robot.designComponents, data.design.components);

    // Modification fee: only charged if the design actually changed
    let modFee = 0;
    if (designChanged && !GOD.freeBuilding) {
      modFee = Math.max(
        GOD.modificationFeeMin,
        Math.round(oldCost * GOD.modificationFeePercent),
      );
    }

    const totalNeeded = Math.max(0, diff) + modFee;

    // Check if player can afford upgrade + modification fee
    if (totalNeeded > 0 && this.resources < totalNeeded) {
      this.showCantAffordFeedback();
      return;
    }

    // Apply cost difference + modification fee
    const totalSpend = diff + modFee;
    if (totalSpend > 0) {
      if (!this.gameScene.spendResources(totalSpend)) return;
    } else if (totalSpend < 0) {
      this.gameScene.addResources(-totalSpend);
    }

    // Rebuild robot in-place: remove old components, add new ones
    const posX = robot.x;
    const posY = robot.y;

    // Destroy old graphics (container + scene-level bars)
    robot.container.destroy();
    robot.destroyBars();

    // Re-create container and components
    robot.components = [];
    robot.container = this.gameScene.add.container(posX, posY);
    robot.container.setDepth(20);

    // Re-create debug overlays on the new container
    robot.createDebugOverlays();

    for (const comp of data.design.components) {
      robot.addComponent(comp.type, comp.lx, comp.ly, comp.rotation ?? 0, comp.flipX ?? false, comp.flipY ?? false, comp.evoLevel ?? 0);
    }
    // Set heading BEFORE resetHpEnergy so stability uses correct flight axis
    const editDirComp = data.design.components.find(c => c.type === ComponentType.DIRECTION);
    if (editDirComp) {
      const rot = editDirComp.rotation ?? 0;
      robot.heading = rot;
      robot.buildHeading = rot;
      robot.recalcStats();
    }
    robot.resetHpEnergy();
    robot.updateDebugOverlays();

    // Capture old name before overwriting (needed for auto-save logic)
    const oldName = robot.designName;

    // Update stored design data
    robot.designComponents = [...data.design.components];
    robot.designRules = [...data.design.rules];
    robot.designCost = newCost;
    robot.designName = data.name;

    // Update rules in the manager
    this.robotManager.setRules(robot, [...data.design.rules]);

    // Persist modified design in the design library (campaign persistence)
    const matchIdx = DesignStore.findMatchingDesign(data.design);
    if (matchIdx >= 0) {
      // Same design layout exists — update its name if changed
      const existing = DesignStore.get(matchIdx);
      if (existing && existing.name !== data.name) {
        DesignStore.update(matchIdx, data.name, data.design);
      }
    } else {
      // Different design: add as new entry (with suffix if name taken)
      let saveName = data.name;
      if (DesignStore.findIndexByName(saveName) >= 0 && saveName !== oldName) {
        let suffix = 2;
        while (DesignStore.findIndexByName(`${data.name} v${suffix}`) >= 0) suffix++;
        saveName = `${data.name} v${suffix}`;
      } else if (DesignStore.findIndexByName(saveName) >= 0) {
        // Same name, different design — update in place
        DesignStore.update(DesignStore.findIndexByName(saveName), saveName, data.design);
        robot.designName = saveName;
        this.refreshRoster();
        this.refreshSavedDesigns();
        return;
      }
      DesignStore.add(saveName, data.design);
      robot.designName = saveName;
    }

    this.refreshRoster();
    this.refreshSavedDesigns();
  };

  private onGameOver = (won: boolean): void => {
    this.gameOver = true;
    this.startButton.setVisible(false);
    this.workshopPanel.setVisible(false);
    this.combatDeployPanel.setVisible(false);
    this.cancelPlacement();
    this.sellIcon.setVisible(false);
    this.cancelIcon.setVisible(false);
    this.soundPanel?.setVisible(false);

    this.gameOverOverlay.show({
      won,
      scene: this,
      robotManager: this.robotManager,
      rollbackToSnapshot: () => this.rollbackToSnapshot(),
    });
  };

  // ── Saved designs list ──

  private refreshSavedDesigns(): void {
    // Clear old items
    for (const item of this.savedDesignItems) item.destroy();
    this.savedDesignItems = [];

    const ITEM_H = 50; // taller to fit preview thumbnail
    const designs = DesignStore.getForMode();
    for (let i = 0; i < designs.length; i++) {
      const saved = designs[i];
      const designIndex = DesignStore.globalIndex(i);
      const y = i * (ITEM_H + 4);
      const item = this.add.container(8, y);

      const bg = this.add.rectangle(0, 0, 124, ITEM_H, 0x223344, 1)
        .setOrigin(0, 0).setStrokeStyle(1, 0x446688)
        .setInteractive({ useHandCursor: true });
      item.add(bg);

      // Robot preview thumbnail (left side, added after bg so it renders on top)
      const preview = drawRobotPreview(this, saved.design.components, 36);
      preview.setPosition(22, ITEM_H / 2);
      item.add(preview);

      // Name label (right of preview) — click to rename
      const nameLabel = this.add.text(46, 4, saved.name, {
        fontSize: '9px', color: '#44ccff', fontFamily: 'monospace', fontStyle: 'bold',
      }).setInteractive({ useHandCursor: true });
      nameLabel.on('pointerover', () => nameLabel.setColor('#88eeff'));
      nameLabel.on('pointerout', () => nameLabel.setColor('#44ccff'));
      nameLabel.on('pointerdown', (p: Phaser.Input.Pointer) => {
        p.event.stopPropagation();
        SoundManager.play('click');
        showTextInputDialog(this, 'Rename design', saved.name, (newName) => {
          if (newName !== saved.name) {
            DesignStore.update(designIndex, newName.substring(0, 20), saved.design);
            this.refreshSavedDesigns();
          }
        }, { maxLength: 20 });
      });

      // Cost + parts info
      const costLabel = this.add.text(46, 16, `$${displayCost(saved.design.cost)}  ${saved.design.components.length}p`, {
        fontSize: '8px', color: '#888899', fontFamily: 'monospace',
      });

      // Rules count
      const rulesLabel = this.add.text(46, 28, `${saved.design.rules.length} rules`, {
        fontSize: '7px', color: '#666688', fontFamily: 'monospace',
      });

      bg.on('pointerover', () => bg.setFillStyle(0x334466));
      bg.on('pointerout', () => bg.setFillStyle(0x223344));
      bg.on('pointerdown', () => { SoundManager.play('clickSelect'); this.selectSavedDesign(saved.design, saved.name, item); });

      // Edit design button (PNG icon)
      const editBtn = this.add.image(110, 13, 'icon_edit')
        .setDisplaySize(24, 24)
        .setInteractive({ useHandCursor: true });
      editBtn.on('pointerover', () => editBtn.setTint(0x88eeff));
      editBtn.on('pointerout', () => editBtn.clearTint());
      editBtn.on('pointerdown', (p: Phaser.Input.Pointer) => {
        p.event.stopPropagation();
        SoundManager.play('click');
        this.editSavedDesign(designIndex);
      });

      // Delete design button (PNG icon)
      const delBtn = this.add.image(110, 37, 'icon_delete')
        .setDisplaySize(24, 24)
        .setInteractive({ useHandCursor: true });
      delBtn.on('pointerover', () => delBtn.setTint(0xff6666));
      delBtn.on('pointerout', () => delBtn.clearTint());
      delBtn.on('pointerdown', (p: Phaser.Input.Pointer) => {
        p.event.stopPropagation();
        SoundManager.play('clickCancel');
        DesignStore.remove(designIndex);
        this.refreshSavedDesigns();
      });

      item.add(nameLabel);
      item.add(costLabel);
      item.add(rulesLabel);
      item.add(editBtn);
      item.add(delBtn);
      this.savedDesignsContainer.add(item);
      this.savedDesignItems.push(item);
    }

    // Position the roster below saved designs
    const rosterY = this.savedDesignsContainer.y + designs.length * 54 + 8;
    this.rosterContainer.setY(rosterY);

    // Add DEPLOYED label if not already positioned
    // (We handle it by positioning rosterContainer dynamically)
  }

  private selectSavedDesign(design: RobotDesign, name: string = '', sourceContainer?: Phaser.GameObjects.Container | null): void {
    if (this.phase !== GamePhase.WORKSHOP) return;
    const cost = displayCost(design.cost);
    if (this.resources < cost) {
      this.showCantAffordFeedback(sourceContainer);
      return;
    }
    this.cancelPlacement();
    this.placementDesign = design;
    this.placementDesignName = name;
    this.createPlacementGhost(design.components);
    this.phaseText.setText(t('hud.phase.placingCustom')).setColor('#44ccff');
    this.warnIfBudgetTight(cost);
  }

  /** Max robots to show all sensor overlays; above this only selected robot shows sensors */
  private static readonly SENSOR_OVERLAY_LIMIT = 20;

  /** When many robots deployed, show sensor only for the newly selected robot */
  private refreshSensorForSelection(selected: Robot | null): void {
    if (!this.robotManager) return;
    const robots = this.robotManager.robots;
    const aliveCount = robots.filter(r => r.alive).length;
    if (aliveCount <= UIScene.SENSOR_OVERLAY_LIMIT) return; // all sensors already visible
    for (const robot of robots) {
      if (!robot.alive) continue;
      const show = robot === selected;
      if (robot.showSensorCircle !== show) {
        robot.showSensorCircle = show;
        robot.updateDebugOverlays();
      }
    }
  }

  private setSensorCirclesVisible(visible: boolean): void {
    if (!this.robotManager) return;
    const robots = this.robotManager.robots;
    const aliveCount = robots.filter(r => r.alive).length;
    const tooMany = aliveCount > UIScene.SENSOR_OVERLAY_LIMIT;
    for (const robot of robots) {
      if (!robot.alive) continue;
      // When many robots are deployed, only show sensor for the selected robot
      robot.showSensorCircle = visible && (!tooMany || robot === this.selectedRobot);
      robot.updateDebugOverlays();
    }
  }

  private editSavedDesign(designIndex: number): void {
    if (this.phase !== GamePhase.WORKSHOP) return;
    const saved = DesignStore.get(designIndex);
    if (!saved) return;
    this.cancelPlacement();
    // Store which design index we're editing so we can update it on save
    this.editingDesignIndex = designIndex;
    const editData: WorkshopEditData = {
      components: [...saved.design.components],
      rules: [...saved.design.rules],
      name: saved.name,
      // No editingRobotId — this is a design edit, not a deployed robot edit
    };
    if (this.gameScene) this.registry.set('gameResources', this.gameScene.resources);
    this.scene.pause('GameScene');
    this.scene.pause('UIScene');
    this.scene.launch('WorkshopScene', editData);
  }

  // ── Deployed robots roster ──

  private refreshRoster(): void {
    // Clear old items
    for (const item of this.rosterItems) item.destroy();
    this.rosterItems = [];

    if (!this.robotManager) return;

    const robots = this.robotManager.robots.filter(r => r.alive);
    this.rosterHeaderText.setText(t('roster.deployed', { n: robots.length }));
    const ITEM_H = 34;

    for (let i = 0; i < robots.length; i++) {
      const robot = robots[i];
      const y = 12 + i * ITEM_H;
      const item = this.add.container(8, y);

      // Color dot based on HP ratio
      const hpRatio = robot.totalHp > 0 ? robot.currentHp / robot.totalHp : 1;
      const dotColor = hpRatio > 0.5 ? 0x44ff44 : hpRatio > 0.25 ? 0xffaa00 : 0xff4444;
      item.add(this.add.circle(4, 6, 4, dotColor));

      // Robot label (clickable — selects for inspection)
      const displayName = robot.designName || robot.id.replace('robot_', 'R');
      const label = this.add.text(14, 0, `${displayName} ${Math.floor(robot.currentHp)}hp`, {
        fontSize: '9px', color: '#cccccc', fontFamily: 'monospace',
      }).setInteractive({ useHandCursor: true });

      label.on('pointerover', () => label.setColor('#ffffff'));
      label.on('pointerout', () => label.setColor('#cccccc'));
      label.on('pointerdown', () => {
        SoundManager.play('clickSelect');
        this.selectedRobot = robot;
        this.showSellIcon(robot);
        EventBus.emit(GameEvents.ROBOT_SELECTED, robot);
      });
      item.add(label);

      // Repair/Rebuild button (only during workshop + robot damaged)
      if (this.phase === GamePhase.WORKSHOP && (robot.needsRepair() || robot.needsRebuild())) {
        const isRebuild = robot.needsRebuild();
        const cost = isRebuild ? robot.getRebuildCost() : robot.getRepairCost();
        const canAfford = this.resources >= cost;
        const btnColor = isRebuild ? '#cc8844' : '#44cc66';
        const btnHover = isRebuild ? '#ffaa66' : '#66ff88';
        const repBtn = this.add.text(72, 8, isRebuild ? '⟳' : '+', {
          fontSize: '18px', color: canAfford ? btnColor : '#667766', fontFamily: 'monospace', fontStyle: 'bold',
        }).setInteractive({ useHandCursor: true });
        repBtn.on('pointerover', () => repBtn.setColor(canAfford ? btnHover : '#667766'));
        repBtn.on('pointerout', () => repBtn.setColor(canAfford ? btnColor : '#667766'));
        repBtn.on('pointerdown', () => {
          if (isRebuild) this.rebuildRobot(robot);
          else this.repairRobot(robot);
        });
        item.add(repBtn);
      }

      // Edit circle (blue)
      const editDot = this.add.circle(108, 7, 3, 0x2266aa)
        .setStrokeStyle(1, 0x44aaff)
        .setInteractive({ useHandCursor: true });
      editDot.on('pointerover', () => editDot.setFillStyle(0x4488cc));
      editDot.on('pointerout', () => editDot.setFillStyle(0x2266aa));
      editDot.on('pointerdown', () => { SoundManager.play('click'); this.openWorkshopForEdit(robot); });
      item.add(editDot);

      // Delete circle (red)
      const delDot = this.add.circle(118, 7, 3, 0xaa2222)
        .setStrokeStyle(1, 0xff4444)
        .setInteractive({ useHandCursor: true });
      delDot.on('pointerover', () => delDot.setFillStyle(0xcc4444));
      delDot.on('pointerout', () => delDot.setFillStyle(0xaa2222));
      delDot.on('pointerdown', () => { SoundManager.play('clickCancel'); this.deleteRobot(robot); });
      item.add(delDot);

      this.rosterScrollContent.add(item);
      this.rosterItems.push(item);
    }

    // ── REPAIR ALL button (below roster items) ──
    if (this.repairAllBtn) { this.repairAllBtn.destroy(); this.repairAllBtn = null; }
    if (this.phase === GamePhase.WORKSHOP && robots.some(r => r.needsRepair())) {
      const totalCost = robots.filter(r => r.needsRepair()).reduce((s, r) => s + r.getRepairCost(), 0);
      const canAfford = this.resources >= totalCost;
      const btnY = 16 + robots.length * ITEM_H;
      const btn = this.add.container(8, btnY);

      const bw = 118, bh = 20, br = 4;
      const btnBg = this.add.graphics();
      btnBg.fillStyle(canAfford ? 0x22aa44 : 0x445544, 0.85);
      btnBg.fillRoundedRect(0, 0, bw, bh, br);
      btnBg.lineStyle(1, canAfford ? 0x66ff88 : 0x556655, 1);
      btnBg.strokeRoundedRect(0, 0, bw, bh, br);
      btn.add(btnBg);

      const btnLabel = this.add.text(bw / 2, bh / 2, t('roster.repairAll', { cost: totalCost }), {
        fontSize: '9px', color: canAfford ? '#ffffff' : '#889988', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5);
      btn.add(btnLabel);

      const btnHit = this.add.zone(bw / 2, bh / 2, bw, bh).setInteractive({ useHandCursor: canAfford });
      if (canAfford) {
        btnHit.on('pointerover', () => {
          btnBg.clear();
          btnBg.fillStyle(0x33cc55, 0.95);
          btnBg.fillRoundedRect(0, 0, bw, bh, br);
          btnBg.lineStyle(1, 0x88ffaa, 1);
          btnBg.strokeRoundedRect(0, 0, bw, bh, br);
        });
        btnHit.on('pointerout', () => {
          btnBg.clear();
          btnBg.fillStyle(0x22aa44, 0.85);
          btnBg.fillRoundedRect(0, 0, bw, bh, br);
          btnBg.lineStyle(1, 0x66ff88, 1);
          btnBg.strokeRoundedRect(0, 0, bw, bh, br);
        });
        btnHit.on('pointerdown', () => { this.repairAllRobots(); });
      }
      btn.add(btnHit);

      this.rosterScrollContent.add(btn);
      this.repairAllBtn = btn;
    }

    // ── Compute scroll bounds and apply mask ──
    const repairExtra = this.repairAllBtn ? 30 : 0;
    const contentH = 16 + robots.length * ITEM_H + repairExtra;
    const rosterWorldY = this.rosterContainer.y;
    const availableH = CANVAS_HEIGHT - rosterWorldY - 8;
    this.rosterMaxScroll = Math.max(0, contentH - availableH);

    // Clamp current scroll position
    this.rosterScrollY = Phaser.Math.Clamp(this.rosterScrollY, -this.rosterMaxScroll, 0);
    this.rosterScrollContent.setY(this.rosterScrollY);

    // Update mask to clip roster to available area
    if (this.rosterMask) {
      this.rosterMask.destroy();
      this.rosterMask = null;
    }
    const panelW = (this as any)._rosterPanelW ?? 140;
    const maskShape = this.make.graphics({ x: 0, y: 0 });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, rosterWorldY, panelW, availableH);
    this.rosterMask = maskShape.createGeometryMask();
    this.rosterScrollContent.setMask(this.rosterMask);

    // Resize hit zone to match available area
    const hitZone = (this as any)._rosterHitZone as Phaser.GameObjects.Rectangle | undefined;
    if (hitZone) {
      hitZone.setPosition(panelW / 2, availableH / 2);
      hitZone.setSize(panelW, availableH);
    }

    // Show scroll indicator if content overflows
    this.updateRosterScrollIndicator(robots.length, availableH, contentH);
  }

  /** Show a small scroll indicator when roster overflows */
  private updateRosterScrollIndicator(robotCount: number, availH: number, contentH: number): void {
    // Remove old indicator
    const oldInd = (this as any)._rosterScrollInd as Phaser.GameObjects.Text | undefined;
    if (oldInd) { oldInd.destroy(); (this as any)._rosterScrollInd = null; }

    if (contentH <= availH || robotCount === 0) return;

    const ind = this.add.text(4, availH - 14, t('roster.scroll', { n: robotCount }), {
      fontSize: '8px', color: '#667788', fontFamily: 'monospace',
    });
    this.rosterContainer.add(ind);
    (this as any)._rosterScrollInd = ind;
  }

  // ── Update loop ──

  update(): void {
    if (!this.waveManager) {
      this.waveManager = this.registry.get('waveManager') as WaveManager;
    }
    if (!this.robotManager) {
      this.robotManager = this.registry.get('robotManager') as RobotManager;
    }
    if (!this.gameScene) {
      this.gameScene = this.registry.get('gameScene') as GameScene;
    }

    // FPS counter
    this.fpsText.setText(`${Math.round(this.game.loop.actualFps)} FPS`);
    // Mini profiler overlay (F3 to toggle)
    MiniProfiler.render(this.profilerText);

    // Live-update HUD positions from GodMode sliders
    if (this.enemiesText) this.enemiesText.setX(GOD.hudEnemiesX);
    if (this.baseHpText) this.baseHpText.setX(GOD.hudBaseX);
    // Live-update HP bar frame (fond) — independent of segmented bar size
    if (this.hpBarFrame) {
      this.hpBarFrame.setDisplaySize(GOD.hpBarFrameW, GOD.hpBarFrameH);
      const frameCx = GOD.hudBarX + GOD.hpBarFrameW / 2 + GOD.hpBarFrameOffX;
      const frameCy = this.hpBarFrameCy + GOD.hpBarFrameOffY;
      this.hpBarFrame.setPosition(frameCx, frameCy);
    }
    // Live-update segmented HP bar size from GodMode sliders
    if (this.baseHpBarBg) {
      const barX = GOD.hudBarX;
      const barW = GOD.hudBarW;
      const barH = GOD.hudBarH;
      const barY = this.baseHpBarBg.y;
      const needsRebuild = this._lastBarW !== barW || this._lastBarH !== barH || this._lastBarX !== barX;
      if (needsRebuild) {
        this._lastBarX = barX;
        this._lastBarW = barW;
        this._lastBarH = barH;
        this.baseHpBarBg.setPosition(barX, barY).setSize(barW, barH);
        if (this.baseHpBarBorder) this.baseHpBarBorder.setPosition(barX, barY).setSize(barW, barH);
        this.buildBaseHpSegments(barX, barY, barW, barH);
      }
    }

    if (this.waveManager && this.phase === GamePhase.WAVE) {
      this.enemiesText.setText(t('hud.enemies', { n: this.waveManager.enemiesRemaining }));
    }

    // Refresh saved designs list when count changes
    const dCount = DesignStore.countForMode();
    if (dCount !== this.lastDesignCount) {
      this.refreshSavedDesigns();
      this.lastDesignCount = dCount;
    }

    // Refresh roster when robot count changes, or every ~30 frames during WAVE only
    // (during WORKSHOP robot count doesn't change — no need to rebuild 435+ objects)
    if (this.robotManager) {
      let count = 0;
      for (const r of this.robotManager.robots) { if (r.alive) count++; }
      const countChanged = count !== this.lastRobotCount;
      if (countChanged) {
        this.refreshRoster();
        this.lastRobotCount = count;
        this.rosterTickCounter = 0;
      } else if (this.phase === GamePhase.WAVE) {
        this.rosterTickCounter = (this.rosterTickCounter ?? 0) + 1;
        if (this.rosterTickCounter >= 30) {
          this.refreshRoster();
          this.rosterTickCounter = 0;
        }
      }
    }

    // Update placement ghost position to follow cursor, snapping to grid
    if (this.placementGhost && (this.placementPreset || this.placementDesign)) {
      const pointer = this.input.activePointer;
      const gameCam = this.gameScene?.cameras?.main;
      let wx = pointer.x;
      let wy = pointer.y;
      if (gameCam) {
        const wp = gameCam.getWorldPoint(pointer.x, pointer.y);
        wx = wp.x;
        wy = wp.y;
      }
      // Snap to tile center (world coords)
      const col = Math.floor(wx / TILE_SIZE);
      const row = Math.floor(wy / TILE_SIZE);
      const snapWorldX = col * TILE_SIZE + TILE_SIZE / 2;
      const snapWorldY = row * TILE_SIZE + TILE_SIZE / 2;

      // Convert snapped world coords back to screen coords for UIScene
      // Using worldView: worldView.x/y is the top-left world coord visible at screen (0,0)
      let screenX = snapWorldX;
      let screenY = snapWorldY;
      if (gameCam) {
        const wv = gameCam.worldView;
        screenX = (snapWorldX - wv.x) / wv.width * gameCam.width;
        screenY = (snapWorldY - wv.y) / wv.height * gameCam.height;
      }
      this.placementGhost.setPosition(screenX, screenY);

      // Tint green/red based on valid placement per robot type
      const comps = this.placementPreset?.components ?? this.placementDesign?.components ?? [];
      const wings = comps.some(c => c.type === ComponentType.WING || c.type === ComponentType.TURBINE);
      const wheels = comps.some(c => c.type === ComponentType.WHEEL);
      const stabLegs = comps.some(c => c.type === ComponentType.STAB_LEGS);
      let valid = false;
      if (this.gameScene) {
        if (wings) {
          valid = this.gameScene.isFlyingPlacementTile(col, row);
        } else if (wheels || stabLegs) {
          valid = this.gameScene.isMobilePlacementTile(col, row);
        } else {
          valid = this.gameScene.isFixedPlacementTile(col, row);
        }
      }
      // Anchor rule: mobile/flying robots must be within sensor range of a fixed robot OR within base deploy range
      if (valid && (wheels || wings)) {
        const snapX = col * TILE_SIZE + TILE_SIZE / 2;
        const snapY = row * TILE_SIZE + TILE_SIZE / 2;
        const nearRobotAnchor = this.robotManager.robots.some(r => {
          if (!r.alive || r.canFly() || r.moveForce > 0) return false;
          const adx = r.x - snapX, ady = r.y - snapY;
          const anchorRange = Math.max(r.sensorRange, GOD.eyesightRange);
          return Math.sqrt(adx * adx + ady * ady) <= anchorRange;
        });
        const baseRange = GOD.baseDeployRange * TILE_SIZE;
        const nearBase = baseRange > 0 && BASE_POSITIONS.some(bp => {
          const bdx = bp.x - snapX, bdy = bp.y - snapY;
          return Math.sqrt(bdx * bdx + bdy * bdy) <= baseRange;
        });
        if (!nearRobotAnchor && !nearBase) valid = false;
      }
      this.placementGhost.setAlpha(valid ? 0.6 : 0.25);
    }

    // Update GOD mode preview entities
    this.godModePanel.updatePreviews(this.game.loop.delta);

    // Update inspector if a robot is selected
    if (this.selectedRobot && this.selectedRobot.alive) {
      this.showSellIcon(this.selectedRobot);
    } else if (this.selectedRobot && !this.selectedRobot.alive) {
      this.selectedRobot = null;
      this.sellIcon.setVisible(false);
      this.cancelIcon.setVisible(false);
      this.repairIcon.setVisible(false);
      this.rebuildIcon.setVisible(false);
      this.modifyIcon.setVisible(false);

    }

    // Context-sensitive cursor hint
    this.updateCursorHint();
  }

  /** Context-sensitive cursor tooltip: shows available actions near the pointer */
  private updateCursorHint(): void {
    if (!this.cursorHint) return;

    // Hide when context help is off, or any overlay is open, or game over
    if (!SaveManager.getSettings().contextHelp ||
        this.godModePanel.isOpen || this.soundPanelOpen || this.pauseMenu?.visible || this.gameOver) {
      this.cursorHint.setVisible(false);
      return;
    }

    const pointer = this.input.activePointer;
    const gameCam = this.gameScene?.cameras?.main;
    if (!gameCam) { this.cursorHint.setVisible(false); return; }

    // Convert screen → world coords
    const wp = gameCam.getWorldPoint(pointer.x, pointer.y);
    const col = Math.floor(wp.x / TILE_SIZE);
    const row = Math.floor(wp.y / TILE_SIZE);

    // Skip if pointer is over UI zones (workshop panel, top HUD)
    if (this.workshopPanel.x >= 0 && pointer.x < 140) {
      this.cursorHint.setVisible(false);
      return;
    }

    // Build hint lines
    const hints = this.getCursorHints(col, row, wp.x, wp.y);
    if (hints.length === 0) {
      this.cursorHint.setVisible(false);
      this.lastHintText = '';
      return;
    }

    const text = hints.join('\n');
    // Only update text object when content changes (perf)
    if (text !== this.lastHintText || col !== this.lastHintCol || row !== this.lastHintRow) {
      this.cursorHint.setText(text);
      this.lastHintText = text;
      this.lastHintCol = col;
      this.lastHintRow = row;
    }

    // Position near pointer with offset, clamped to screen
    const offX = 16;
    const offY = -8;
    let hx = pointer.x + offX;
    let hy = pointer.y + offY;
    // Clamp to avoid going off-screen
    const tw = this.cursorHint.width + 10;
    const th = this.cursorHint.height + 6;
    if (hx + tw > CANVAS_WIDTH) hx = pointer.x - offX - tw;
    if (hy - th < 0) hy = pointer.y + 20;
    this.cursorHint.setPosition(hx, hy);
    this.cursorHint.setVisible(true);
  }

  /** Determine context-sensitive hint lines for the tile/entity under cursor.
   *  Priority: dev modes > fog > placement > entities > decorations > phase guidance */
  private getCursorHints(col: number, row: number, wx: number, wy: number): string[] {
    const hints: string[] = [];
    const isWorkshop = this.phase === GamePhase.WORKSHOP || this.phase === GamePhase.INTERMISSION;
    const isWave = this.phase === GamePhase.WAVE;

    // ── Dev modes take priority ──
    if (devNukeMode) return [t('cursor.devNuke')];
    if (devDestroyMode) return [t('cursor.devDestroy')];
    if (devMoveMode) return [t('cursor.devMove')];

    // Out of bounds
    const mapRows = MAP_GRID.length;
    const mapCols = MAP_GRID[0]?.length ?? 0;
    if (col < 0 || col >= mapCols || row < 0 || row >= mapRows) return hints;

    // Fog of war — unexplored
    if (this.gameScene && !this.gameScene.isTileVisible(col, row)) {
      return [t('cursor.fogHidden')];
    }

    // ── Placement mode active — guide the deploy ──
    if (this.placementPreset || this.placementDesign) {
      const comps = this.placementPreset?.components ?? this.placementDesign?.components ?? [];
      const wings = comps.some(c => c.type === ComponentType.WING || c.type === ComponentType.TURBINE);
      const wheels = comps.some(c => c.type === ComponentType.WHEEL);
      const stabLegs = comps.some(c => c.type === ComponentType.STAB_LEGS);
      let valid = false;
      if (this.gameScene) {
        if (wings) valid = this.gameScene.isFlyingPlacementTile(col, row);
        else if (wheels || stabLegs) valid = this.gameScene.isMobilePlacementTile(col, row);
        else valid = this.gameScene.isFixedPlacementTile(col, row);
      }
      return [valid ? t('cursor.placingValid') : t('cursor.placingInvalid')];
    }

    // ── Cracked tile ──
    if (this.gameScene?.crackedTiles?.isCracked(col, row) && isWorkshop) {
      return [t('cursor.crackedTile')];
    }

    // ── Robot under cursor ──
    if (this.robotManager) {
      for (const robot of this.robotManager.robots) {
        if (!robot.alive) continue;
        if (robot.distanceTo(wx, wy) < 20) {
          const hp = Math.ceil(robot.totalHp);
          const max = Math.ceil(robot.components.reduce((s, c) => s + c.compMaxHp, 0));
          hints.push(t('cursor.robotInfo', { name: robot.designName || 'Robot', hp: String(hp), max: String(max) }));
          if (isWorkshop) hints.push(t('cursor.robotSelect'));
          return hints;
        }
      }
    }

    // ── Enemy under cursor (wave phase) ──
    if (isWave && this.waveManager) {
      for (const enemy of this.waveManager.enemies) {
        if (!enemy.alive) continue;
        const dx = wx - enemy.body.x;
        const dy = wy - enemy.body.y;
        if (dx * dx + dy * dy < 25 * 25) {
          const eLabel = t(`enemy.${enemy.enemyType}`);
          hints.push(t('cursor.enemyInfo', { type: eLabel, hp: String(Math.ceil(enemy.hp)), max: String(Math.ceil(enemy.maxHp)) }));
          return hints;
        }
      }
    }

    // ── Player base ──
    const pb = this.registry.get('playerBase') as { x: number; y: number; alive: boolean; hp: number; maxHp: number } | undefined;
    if (pb && pb.alive) {
      const dx = wx - pb.x;
      const dy = wy - pb.y;
      if (dx * dx + dy * dy < TILE_SIZE * TILE_SIZE) {
        hints.push(t('cursor.baseInfo', { hp: String(Math.ceil(pb.hp)), max: String(Math.ceil(pb.maxHp)) }));
        if (isWorkshop) hints.push(t('cursor.baseUpgrade'));
        return hints;
      }
    }

    // ── Decoration / resource on this tile ──
    const decor = DECOR_INDEX.get(decorKey(col, row));
    if (decor) {
      const yields = DECOR_RESOURCE_YIELDS[decor.type];
      const interaction = DECOR_INTERACTIONS[decor.type];
      const dLabel = t(`decor.${decor.type}`);
      if (yields) {
        const parts: string[] = [];
        if (yields.co > 0) parts.push(`+${yields.co} Co`);
        if (yields.electro > 0) parts.push(`+${yields.electro} ⚡`);
        hints.push(t('cursor.resourceInfo', { type: dLabel, yield: parts.join(' ') }));
      } else if (interaction?.interaction === DecorInteraction.KILL) {
        hints.push(t('cursor.decorHazard', { type: dLabel }));
      } else if (interaction?.interaction === DecorInteraction.BLOCK) {
        hints.push(t('cursor.decorObstacle', { type: dLabel }));
      }
      if (hints.length > 0) return hints;
    }

    // ── Spawn tile (enemy base) ──
    if (MAP_GRID[row]?.[col] === TileType.SPAWN) {
      hints.push(t('cursor.enemySpawn'));
      return hints;
    }

    // ── Phase-level guidance (no specific entity hovered) ──
    if (isWorkshop) {
      const hasRobots = this.robotManager && this.robotManager.robots.some(r => r.alive);
      if (hasRobots) {
        hints.push(t('cursor.workshopStartWave'));
      } else {
        hints.push(t('cursor.workshopChoose'));
      }
    } else if (this.phase === GamePhase.INTERMISSION) {
      hints.push(t('cursor.intermissionDeploy'));
    } else if (isWave) {
      hints.push(t('cursor.waveInProgress'));
    }

    return hints;
  }

  shutdown(): void {
    UITargetRegistry.unregister('startButton');
    if (this._onDevModeChangedGod) EventBus.off(DEV_MODE_CHANGED, this._onDevModeChangedGod);
    if (this._onDevModeChangedCin) EventBus.off(DEV_MODE_CHANGED, this._onDevModeChangedCin);
    this._onDevModeChangedGod = null;
    this._onDevModeChangedCin = null;
    EventBus.off(GameEvents.ACHIEVEMENT_UNLOCKED);
    this.removeEventListeners();
    this.cinematicDirector?.destroy();
    this.cinematicTriggerManager?.destroy();

    // Clean up extracted modules
    this.godModePanel?.destroy();
    this.robotInspector?.destroy();
    this.gameOverOverlay?.destroy();
    this.workshopPanelModule?.destroy();
    this.rosterPanelModule?.destroy();
  }

  // ── Achievement toast (slide-in from top-right) ──

  private setupAchievementToast(): void {
    EventBus.on(GameEvents.ACHIEVEMENT_UNLOCKED, (data: { id: string; badge: string; def: AchievementDef }) => {
      this.achievementQueue.push(data);
      if (!this.toastProcessing) this.processNextToast();
    });
  }

  private processNextToast(): void {
    if (this.achievementQueue.length === 0) { this.toastProcessing = false; return; }
    this.toastProcessing = true;
    const data = this.achievementQueue.shift()!;
    const def = data.def;
    const tierColor = TIER_COLORS[data.badge as keyof typeof TIER_COLORS] ?? 0xffd700;
    const tierColorStr = `#${tierColor.toString(16).padStart(6, '0')}`;

    // Pause game
    this.scene.pause('GameScene');

    const cx = CANVAS_WIDTH / 2, cy = CANVAS_HEIGHT / 2;
    const W = 340, H = 180;

    const container = this.add.container(cx, cy).setDepth(300).setScale(0);

    // Dim backdrop
    const backdrop = this.add.rectangle(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.5)
      .setInteractive();
    container.add(backdrop);

    // Panel background
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a1e, 0.95);
    bg.fillRoundedRect(-W / 2, -H / 2, W, H, 10);
    bg.lineStyle(2, tierColor, 0.8);
    bg.strokeRoundedRect(-W / 2, -H / 2, W, H, 10);
    container.add(bg);

    // Badge circle (top center, overlapping panel edge)
    const badgeGfx = this.add.graphics();
    badgeGfx.fillStyle(tierColor, 1);
    badgeGfx.fillCircle(0, -H / 2, 18);
    badgeGfx.lineStyle(2, 0xffffff, 0.4);
    badgeGfx.strokeCircle(0, -H / 2, 18);
    container.add(badgeGfx);

    // Trophy icon in badge
    container.add(this.add.text(0, -H / 2, '🏅', {
      fontSize: '18px', fontFamily: 'monospace',
    }).setOrigin(0.5, 0.5));

    // "ACHIEVEMENT UNLOCKED" header
    container.add(this.add.text(0, -H / 2 + 28, t('achievement.unlocked'), {
      fontSize: '10px', color: tierColorStr, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0.5));

    // Achievement name
    container.add(this.add.text(0, -H / 2 + 52, t(`achievement.${def.id}.name`), {
      fontSize: '16px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
      wordWrap: { width: W - 40 }, align: 'center',
    }).setOrigin(0.5, 0.5));

    // Description
    container.add(this.add.text(0, -H / 2 + 76, t(`achievement.${def.id}.desc`), {
      fontSize: '10px', color: '#8899aa', fontFamily: 'monospace',
      wordWrap: { width: W - 40 }, align: 'center',
    }).setOrigin(0.5, 0.5));

    // Reward hint
    container.add(this.add.text(0, -H / 2 + 100, `🎁 ${t('achievement.claimHint')}`, {
      fontSize: '10px', color: '#ffcc44', fontFamily: 'monospace',
    }).setOrigin(0.5, 0.5));

    // OK button
    const btnW = 80, btnH = 30;
    const btnY = H / 2 - 28;
    const btnGfx = this.add.graphics();
    btnGfx.fillStyle(tierColor, 0.8);
    btnGfx.fillRoundedRect(-btnW / 2, btnY - btnH / 2, btnW, btnH, 5);
    container.add(btnGfx);

    const btnText = this.add.text(0, btnY, 'OK', {
      fontSize: '14px', color: '#000000', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0.5);
    container.add(btnText);

    const btnHit = this.add.rectangle(0, btnY, btnW, btnH, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    btnHit.on('pointerover', () => btnGfx.clear().fillStyle(tierColor, 1).fillRoundedRect(-btnW / 2, btnY - btnH / 2, btnW, btnH, 5));
    btnHit.on('pointerout', () => btnGfx.clear().fillStyle(tierColor, 0.8).fillRoundedRect(-btnW / 2, btnY - btnH / 2, btnW, btnH, 5));
    btnHit.on('pointerdown', () => {
      SoundManager.play('click');
      container.destroy();
      this.scene.resume('GameScene');
      this.processNextToast();
    });
    container.add(btnHit);

    // Pop-in animation
    this.tweens.add({
      targets: container,
      scaleX: 1, scaleY: 1,
      duration: 350, ease: 'Back.easeOut',
    });

    SoundManager.play('streak');
  }
}
